import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useDashboardData, useRegenerateSection, useTaskStatus, useCreativeBriefHistory, useSaveCreativeBrief } from '../api/researchApi';
import { toast } from 'react-hot-toast';


const steps = [
  'Project Overview',
  'Product Snapshot',
  'Current Listing Challenges',
  'Target Customer Deep Dive',
  'Barriers To Purchase',
  'Brand Voice & Tone',
  'USP(Unique SellingPropositions)',
  '5-Second Wow Factor',
  'Key Features (With Context)',
  'Top 6 Selling Points (With Strategic Justification)',
  'Competitive Landscape',
  'Search & Keywords Strategy',
  'Brand Story, Values & Purpose',
  'Design Direction',
  'Final Notes & Strategic Callouts',
];

const StepIcon = ({ status }: { status: 'done' | 'current' | 'upcoming' }) => {
  if (status === 'done') {
    // Green check SVG
    return (
      <svg width="30" height="33" viewBox="0 0 30 33" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_d_41_203)">
          <circle cx="14" cy="14.3846" r="14" fill="#4ADE80"/>
          <path d="M9.5 14.5L13 18L19 12" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
        </g>
        <defs>
          <filter id="filter0_d_41_203" x="-2" y="0.384613" width="32" height="32" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
            <feFlood floodOpacity="0" result="BackgroundImageFix"/>
            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
            <feOffset dy="2"/>
            <feGaussianBlur stdDeviation="1"/>
            <feComposite in2="hardAlpha" operator="out"/>
            <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_41_203"/>
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_41_203" result="shape"/>
          </filter>
        </defs>
      </svg>
    );
  }
  // Spinner/star SVG (purple for current, faded for upcoming)
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="33" viewBox="0 0 30 33" fill="none">
      <g filter="url(#filter0_d_41_203)">
        <circle cx="14" cy="14.3846" r="14" fill="white"/>
        <path d="M14 10.5585C14.2767 10.5585 14.5422 10.4486 14.7379 10.2529C14.9335 10.0572 15.0435 9.7918 15.0435 9.51505V7.42809C15.0435 7.15134 14.9335 6.88593 14.7379 6.69024C14.5422 6.49455 14.2767 6.38461 14 6.38461C13.7233 6.38461 13.4578 6.49455 13.2621 6.69024C13.0665 6.88593 12.9565 7.15134 12.9565 7.42809V9.51505C12.9565 9.7918 13.0665 10.0572 13.2621 10.2529C13.4578 10.4486 13.7233 10.5585 14 10.5585ZM11.4052 10.2872L9.93043 8.8194C9.73123 8.6227 9.46256 8.51241 9.18261 8.51241C8.90266 8.51241 8.63399 8.6227 8.43478 8.8194C8.33724 8.91574 8.25991 9.03059 8.20735 9.15721C8.15479 9.28384 8.12805 9.41969 8.1287 9.55679C8.13225 9.83072 8.24198 10.0926 8.43478 10.2872L9.93043 11.762C10.1259 11.9578 10.3912 12.0678 10.6678 12.0681C10.8741 12.0679 11.0756 12.0066 11.2471 11.8919C11.4185 11.7773 11.5521 11.6144 11.631 11.4238C11.7099 11.2333 11.7306 11.0236 11.6904 10.8213C11.6502 10.619 11.551 10.4332 11.4052 10.2872ZM6 14.3846C6 14.6614 6.10994 14.9268 6.30563 15.1225C6.50132 15.3182 6.76673 15.4281 7.04348 15.4281H9.13043C9.40718 15.4281 9.6726 15.3182 9.86829 15.1225C10.064 14.9268 10.1739 14.6614 10.1739 14.3846C10.1739 14.1079 10.064 13.8425 9.86829 13.6468C9.6726 13.4511 9.40718 13.3411 9.13043 13.3411H7.04348C6.76673 13.3411 6.50132 13.4511 6.30563 13.6468C6.10994 13.8425 6 14.1079 6 14.3846ZM10.5217 16.8333C10.3779 16.8324 10.2356 16.8617 10.1038 16.9192C9.97197 16.9767 9.85368 17.0612 9.75652 17.1672L8.27478 18.6142C8.08106 18.8107 7.97246 19.0756 7.97246 19.3516C7.97246 19.6275 8.08106 19.8924 8.27478 20.089C8.47162 20.2848 8.73799 20.3947 9.01565 20.3947C9.29332 20.3947 9.55968 20.2848 9.75652 20.089L11.2174 18.6142C11.3609 18.4708 11.4595 18.2887 11.5012 18.0902C11.5428 17.8916 11.5257 17.6852 11.4519 17.4963C11.3781 17.3073 11.2508 17.144 11.0856 17.0262C10.9204 16.9085 10.7244 16.8414 10.5217 16.8333ZM12.9565 21.3411C12.9565 21.6179 13.0665 21.8833 13.2621 22.079C13.4578 22.2747 13.7233 22.3846 14 22.3846C14.2767 22.3846 14.5422 22.2747 14.7379 22.079C14.9335 21.8833 15.0435 21.6179 15.0435 21.3411V19.2542C15.0435 18.9774 14.9335 18.712 14.7379 18.5163C14.5422 18.3206 14.2767 18.2107 14 18.2107C13.7233 18.2107 13.4578 18.3206 13.2621 18.5163C13.0665 18.712 12.9565 18.9774 12.9565 19.2542V21.3411ZM16.8174 17.1672C16.622 17.3629 16.5122 17.6281 16.5122 17.9046C16.5122 18.1811 16.622 18.4464 16.8174 18.642L18.2922 20.1168C18.4885 20.3113 18.7532 20.4211 19.0296 20.4229C19.308 20.4215 19.575 20.3117 19.7739 20.1168C19.9671 19.9202 20.0746 19.6551 20.073 19.3794C20.0746 19.2411 20.0483 19.104 19.9957 18.9761C19.9432 18.8482 19.8653 18.7322 19.767 18.635L18.2922 17.1672C18.0965 16.9718 17.8313 16.8621 17.5548 16.8621C17.2783 16.8621 17.013 16.9718 16.8174 17.1672ZM22 14.3846C22 14.1079 21.8901 13.8425 21.6944 13.6468C21.4987 13.4511 21.2333 13.3411 20.9565 13.3411H18.8696C18.5928 13.3411 18.3274 13.4511 18.1317 13.6468C17.936 13.8425 17.8261 14.1079 17.8261 14.3846C17.8261 14.6614 17.936 14.9268 18.1317 15.1225C18.3274 15.3182 18.5928 15.4281 18.8696 15.4281H20.9565C21.2333 15.4281 21.4987 15.3182 21.6944 15.1225C21.8901 14.9268 22 14.6614 22 14.3846ZM18.8696 8.40896C18.6078 8.42125 18.3598 8.53033 18.1739 8.71505L16.6852 10.2107C16.4898 10.4064 16.38 10.6716 16.38 10.9481C16.38 11.2246 16.4898 11.4898 16.6852 11.6855C16.8821 11.8813 17.1484 11.9913 17.4261 11.9913C17.7037 11.9913 17.9701 11.8813 18.167 11.6855L19.6417 10.2107C19.7975 10.0641 19.9045 9.87311 19.9481 9.66368C19.9917 9.45425 19.9699 9.23647 19.8856 9.03984C19.8014 8.84321 19.6587 8.67723 19.477 8.56438C19.2952 8.45154 19.0832 8.39728 18.8696 8.40896Z" fill="#7C5FBF" opacity={status === 'upcoming' ? 0.3 : 1}/>
      </g>
      <defs>
        <filter id="filter0_d_41_203" x="-2" y="0.384613" width="32" height="32" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
          <feOffset dy="2"/>
          <feGaussianBlur stdDeviation="1"/>
          <feComposite in2="hardAlpha" operator="out"/>
          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.12 0"/>
          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_41_203"/>
          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_41_203" result="shape"/>
        </filter>
      </defs>
    </svg>
  );
};

// Helper function to safely extract text from any data type - NO REPETITION
const safeExtractText = (data: any): string => {
  if (data === null || data === undefined) return "";
  if (typeof data === 'string') {
    // Remove any JSON-like curly braces and quotes, but preserve meaningful content
    return data.replace(/[{}"]/g, '').trim();
  }
  if (typeof data === 'number') return data.toString();
  if (typeof data === 'boolean') return data.toString();
  if (Array.isArray(data)) {
    // For arrays, only take the first meaningful item to avoid repetition
    const firstItem = data.find(item => item && typeof item === 'string' && item.trim().length > 0);
    return firstItem ? safeExtractText(firstItem) : "";
  }
  if (typeof data === 'object') {
    // Try to extract meaningful text from object - prioritize specific fields
    if (data.text) return safeExtractText(data.text);
    if (data.content) return safeExtractText(data.content);
    if (data.name) return safeExtractText(data.name);
    if (data.title) return safeExtractText(data.title);
    if (data.description) return safeExtractText(data.description);
    if (data.point) return safeExtractText(data.point);
    if (data.why) return safeExtractText(data.why);
    // For objects, only take the first meaningful string value to avoid repetition
    const textValues = Object.values(data).filter(val => typeof val === 'string' && val.length > 0);
    return textValues.length > 0 ? safeExtractText(textValues[0]) : "";
  }
  return "";
};

const CreativeBriefPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  // Note: queryClient not needed since popup uses hooks to refresh data
  const [activeStep, setActiveStep] = useState(0);
  const [approvedSteps, setApprovedSteps] = useState<Set<number>>(new Set());

  // Helper function for auto-resizing textareas
  const handleTextareaResize = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const target = e.target as HTMLTextAreaElement;
    // Only resize if user is not actively typing to prevent glitching
    if (!isUserEditing) {
      requestAnimationFrame(() => {
        resizeTextarea(target);
      });
    }
  };

  // Track when user is actively editing to prevent automatic refreshes
  useEffect(() => {
    const handleGlobalFocus = (e: FocusEvent) => {
      if (e.target instanceof HTMLTextAreaElement && !e.target.closest('.fixed')) {
        // Don't track focus on popup textareas to avoid interference
        setIsUserEditing(true);
      }
    };

    const handleGlobalBlur = (e: FocusEvent) => {
      if (e.target instanceof HTMLTextAreaElement && !e.target.closest('.fixed')) {
        // Don't track blur on popup textareas to avoid interference
        // Add a small delay to prevent immediate refresh when switching between fields
        setTimeout(() => {
          setIsUserEditing(false);
        }, 1000);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement && !e.target.closest('.fixed')) {
        // Mark as editing when user presses any key
        setIsUserEditing(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLTextAreaElement && !e.target.closest('.fixed')) {
        // Keep editing state for a short time after key release
        setTimeout(() => {
          if (document.activeElement !== e.target) {
            setIsUserEditing(false);
          }
        }, 500);
      }
    };

    // Add global event listeners
    document.addEventListener('focusin', handleGlobalFocus);
    document.addEventListener('focusout', handleGlobalBlur);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('focusin', handleGlobalFocus);
      document.removeEventListener('focusout', handleGlobalBlur);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Format backend content for readability in textareas:
  // - Split into smaller paragraphs
  // - Preserve numbered/bulleted lists as separate lines with blank lines between
  const formatForDisplay = (raw: string): string => {
    if (!raw) return "";
    let text = String(raw).trim();
    // Normalize Windows newlines
    text = text.replace(/\r\n/g, "\n");
    // If there are no line breaks, insert breaks after sentence endings
    if (!/\n/.test(text)) {
      text = text.replace(/([.!?])\s+(?=[A-Z0-9])/g, "$1\n\n");
    }
    // Ensure blank line before numbered items (1. / 1) ) and bullets (-, •)
    text = text
      .replace(/\s+(?=(\d+[\).]\s))/g, "\n\n")
      .replace(/\s+(?=([-•]\s))/g, "\n\n");
    // Ensure at least one blank line between any lines (convert single to double)
    text = text.replace(/\n(?!\n)/g, "\n\n");
    // Collapse triples+ to double
    text = text.replace(/\n{3,}/g, "\n\n");
    return text.trim();
  };

  // Special formatting for Top 6 Selling Points to convert JSON to plain text
  const formatSellingPointsForDisplay = (raw: string): string => {
    if (!raw) return "";
    let text = String(raw).trim();
    
    console.log('🔧 DEBUG: formatSellingPointsForDisplay input:', text.substring(0, 200) + '...');
    
    // Remove markdown code block formatting if present
    if (text.startsWith('```json') || text.startsWith('```')) {
      text = text.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
      console.log('🔧 DEBUG: Removed markdown formatting:', text.substring(0, 200) + '...');
    }
    
    // Try to parse as JSON array first
    try {
      const parsed = JSON.parse(text);
      console.log('🔧 DEBUG: Successfully parsed JSON:', parsed);
      if (Array.isArray(parsed)) {
        const formatted = parsed
          .map((item, index) => {
            if (typeof item === 'object' && item !== null) {
              const point = item.point || item.Point || '';
              const why = item.why || item.Why || '';
              if (point && why) {
                return `${index + 1}. ${point} - ${why}`;
              } else if (point) {
                return `${index + 1}. ${point}`;
              }
            } else if (typeof item === 'string') {
              return `${index + 1}. ${item}`;
            }
            return '';
          })
          .filter(Boolean)
          .join('\n\n');
        console.log('🔧 DEBUG: Formatted result:', formatted);
        return formatted;
      }
    } catch (e) {
      console.log('🔧 DEBUG: JSON parsing failed:', e);
      // Not JSON, use regular formatting
    }
    
    // If not JSON or parsing failed, use regular formatting
    console.log('🔧 DEBUG: Using regular formatting');
    return formatForDisplay(text);
  };

  // Function to sync demographics data to pie charts
  const syncProductSnapshotToTargetCustomer = (demographicsText: string) => {
    console.log('🔧 DEBUG: ===== SYNC FUNCTION CALLED =====');
    console.log('🔧 DEBUG: syncProductSnapshotToTargetCustomer called with:', demographicsText);
    console.log('🔧 DEBUG: Text length:', demographicsText?.length);
    console.log('🔧 DEBUG: =================================');
    if (!demographicsText) {
      console.log('🔧 DEBUG: No demographicsText, returning early');
      return;
    }
    
    // FORCE UPDATE - Clear any existing data first
    setSyncCompleted(false);
    
    console.log('🔧 DEBUG: Syncing demographics data to pie charts:', demographicsText);
    console.log('🔧 DEBUG: Current genderData before sync:', genderData);
    console.log('🔧 DEBUG: Current ageData before sync:', ageData);
    console.log('🔧 DEBUG: Current syncCompleted flag:', syncCompleted);
    
    // Extract demographic data from the demographics text
    const text = demographicsText.toLowerCase();
    
    // Parse demographics strictly from provided text; no forced overrides
    
    // Extract gender percentages - handle various formats
    // More flexible patterns to catch different formats
    const maleMatch = /(?:male\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*male|male\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*male)/i.exec(text);
    const femaleMatch = /(?:female\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*female|female\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*female)/i.exec(text);
    const otherMatch = /(?:other\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*(?:other|non-binary|nonbinary|nb|prefer not to say)|other\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*(?:other|non-binary|nonbinary|nb|prefer not to say))/i.exec(text);
    
    console.log('🔧 DEBUG: Gender regex patterns tested on text:', text);
    console.log('🔧 DEBUG: Male regex pattern: /(?:male\\s*([0-9]+(?:\\.[0-9]+)?)%|([0-9]+(?:\\.[0-9]+)?)%\\s*male)/i');
    console.log('🔧 DEBUG: Female regex pattern: /(?:female\\s*([0-9]+(?:\\.[0-9]+)?)%|([0-9]+(?:\\.[0-9]+)?)%\\s*female)/i');
    
    console.log('🔧 DEBUG: Gender regex matches:', { maleMatch, femaleMatch, otherMatch });
    console.log('🔧 DEBUG: Text being searched:', text);
    console.log('🔧 DEBUG: Male match details:', maleMatch ? `Found: ${maleMatch[0]}, Group 1: ${maleMatch[1]}, Group 2: ${maleMatch[2]}` : 'No match');
    console.log('🔧 DEBUG: Female match details:', femaleMatch ? `Found: ${femaleMatch[0]}, Group 1: ${femaleMatch[1]}, Group 2: ${femaleMatch[2]}` : 'No match');
    
    // Extract the actual values
    let male = maleMatch ? parseFloat(maleMatch[1] || maleMatch[2] || maleMatch[3] || maleMatch[4]) : NaN;
    let female = femaleMatch ? parseFloat(femaleMatch[1] || femaleMatch[2] || femaleMatch[3] || femaleMatch[4]) : NaN;
    let other = otherMatch ? parseFloat(otherMatch[1] || otherMatch[2] || otherMatch[3] || otherMatch[4]) : NaN;
    
    console.log('🔧 DEBUG: Extracted values before processing:', { male, female, other });
    
    // If no regex matches, try to extract percentages from any text that contains numbers and gender words
    if (!maleMatch && !femaleMatch && !otherMatch) {
      console.log('🔧 DEBUG: No regex matches found, trying alternative parsing...');
      
      // Look for any pattern like "60% male" or "male 60%" or "60% men" etc.
      const altMaleMatch = /(?:([0-9]+(?:\.[0-9]+)?)%\s*(?:male|men|man|boys?))|(?:(?:male|men|man|boys?)\s*([0-9]+(?:\.[0-9]+)?)%)|(?:(?:male|men|man|boys?)\s*([0-9]+(?:\.[0-9]+)?))|(?:([0-9]+(?:\.[0-9]+)?)\s*(?:male|men|man|boys?))/i.exec(text);
      const altFemaleMatch = /(?:([0-9]+(?:\.[0-9]+)?)%\s*(?:female|women|woman|girls?|moms?))|(?:(?:female|women|woman|girls?|moms?)\s*([0-9]+(?:\.[0-9]+)?)%)|(?:(?:female|women|woman|girls?|moms?)\s*([0-9]+(?:\.[0-9]+)?))|(?:([0-9]+(?:\.[0-9]+)?)\s*(?:female|women|woman|girls?|moms?))/i.exec(text);
      
      // If still no matches, try even simpler patterns
      if (!altMaleMatch && !altFemaleMatch) {
        console.log('🔧 DEBUG: Alternative patterns also failed, trying simple number extraction...');
        
        // Extract all numbers from the text
        const numbers = text.match(/([0-9]+(?:\.[0-9]+)?)/g);
        const maleWords = text.match(/\b(male|men|man|boys?)\b/gi);
        const femaleWords = text.match(/\b(female|women|woman|girls?|moms?)\b/gi);
        
        console.log('🔧 DEBUG: Simple extraction results:', { numbers, maleWords, femaleWords });
        
        if (numbers && numbers.length >= 2 && (maleWords || femaleWords)) {
          // If we have at least 2 numbers and gender words, try to match them
          const num1 = parseFloat(numbers[0]);
          const num2 = parseFloat(numbers[1]);
          
          // Simple heuristic: if we have both male and female words, assign the numbers
          if (maleWords && femaleWords) {
            const male = num1;
            const female = num2;
            const total = male + female || 1;
            const norm = (v: number) => Math.max(0, Math.min(100, (v / total) * 100));
            const gd = [
              { name: 'Male', value: parseFloat(norm(male).toFixed(1)), percentage: `${norm(male).toFixed(1)}%`, color: '#6B4EFF' },
              { name: 'Female', value: parseFloat(norm(female).toFixed(1)), percentage: `${norm(female).toFixed(1)}%`, color: '#FF6B9D' },
              { name: 'Other', value: 0, percentage: '0%', color: '#4ECDC4' }
            ];
            setGenderData(gd);
            setPieChartKey(prev => prev + 1);
            setSyncCompleted(true);
            console.log('🔧 DEBUG: Updated gender data using simple number extraction:', gd);
            return;
          }
        }
      }
      
      console.log('🔧 DEBUG: Alternative gender matches:', { altMaleMatch, altFemaleMatch });
      
      if (altMaleMatch || altFemaleMatch) {
        const male = altMaleMatch ? parseFloat(altMaleMatch[1] || altMaleMatch[2] || altMaleMatch[3] || altMaleMatch[4]) : NaN;
        const female = altFemaleMatch ? parseFloat(altFemaleMatch[1] || altFemaleMatch[2] || altFemaleMatch[3] || altFemaleMatch[4]) : NaN;
        
        if (!isNaN(male) || !isNaN(female)) {
          const m = isNaN(male) ? 0 : male;
          const f = isNaN(female) ? 0 : female;
          const total = m + f || 1;
          const norm = (v: number) => Math.max(0, Math.min(100, (v / total) * 100));
          const gd = [
            { name: 'Male', value: parseFloat(norm(m).toFixed(1)), percentage: `${norm(m).toFixed(1)}%`, color: '#6B4EFF' },
            { name: 'Female', value: parseFloat(norm(f).toFixed(1)), percentage: `${norm(f).toFixed(1)}%`, color: '#FF6B9D' },
            { name: 'Other', value: 0, percentage: '0%', color: '#4ECDC4' }
          ];
        setGenderData(gd);
        setPieChartKey(prev => {
          const newKey = prev + 1;
          console.log('🔧 DEBUG: Alternative gender pie chart key incremented from', prev, 'to', newKey);
          return newKey;
        });
        setSyncCompleted(true);
        console.log('🔧 DEBUG: Updated gender data using alternative parsing:', gd);
        console.log('🔧 DEBUG: Gender data state updated, should trigger re-render');
        console.log('🔧 DEBUG: syncCompleted flag set to true for alternative gender parsing');
        }
      }
    }
    
    if (maleMatch || femaleMatch || otherMatch) {
      // Extract values from any of the capture groups
      let male = maleMatch ? parseFloat(maleMatch[1] || maleMatch[2] || maleMatch[3] || maleMatch[4]) : NaN;
      let female = femaleMatch ? parseFloat(femaleMatch[1] || femaleMatch[2] || femaleMatch[3] || femaleMatch[4]) : NaN;
      let other = otherMatch ? parseFloat(otherMatch[1] || otherMatch[2] || otherMatch[3] || otherMatch[4]) : NaN;
      
      console.log('🔧 DEBUG: Parsed values:', { male, female, other });
      
      // If only two present, infer the third to make 100
      const known = [male, female, other].filter(v => !isNaN(v));
      if (known.length >= 2) {
        const sumKnown = (isNaN(male) ? 0 : male) + (isNaN(female) ? 0 : female) + (isNaN(other) ? 0 : other);
        const missingIndex = [male, female, other].findIndex(v => isNaN(v));
        const remaining = Math.max(0, 100 - sumKnown);
        if (missingIndex === 0) male = remaining;
        if (missingIndex === 1) female = remaining;
        if (missingIndex === 2) other = remaining;
      }
      
      // Update gender data
      if (!isNaN(male) || !isNaN(female) || !isNaN(other)) {
        const m = isNaN(male) ? 0 : male;
        const f = isNaN(female) ? 0 : female;
        const o = isNaN(other) ? 0 : other;
        const total = m + f + o || 1;
        const norm = (v: number) => Math.max(0, Math.min(100, (v / total) * 100));
        const gd = [
          { name: 'Male', value: parseFloat(norm(m).toFixed(1)), percentage: `${norm(m).toFixed(1)}%`, color: '#6B4EFF' },
          { name: 'Female', value: parseFloat(norm(f).toFixed(1)), percentage: `${norm(f).toFixed(1)}%`, color: '#FF6B9D' },
          { name: 'Other', value: parseFloat(norm(o).toFixed(1)), percentage: `${norm(o).toFixed(1)}%`, color: '#4ECDC4' }
        ];
        console.log('🔧 DEBUG: About to set gender data:', gd);
        console.log('🔧 DEBUG: Gender data details:', gd.map(item => `${item.name}: ${item.value}%`));
        setGenderData(gd);
        setPieChartKey(prev => {
          const newKey = prev + 1;
          console.log('🔧 DEBUG: Pie chart key incremented from', prev, 'to', newKey);
          return newKey;
        });
        setSyncCompleted(true);
        console.log('🔧 DEBUG: ===== GENDER DATA UPDATED =====');
        console.log('🔧 DEBUG: Updated gender data from Product Snapshot:', gd);
        console.log('🔧 DEBUG: Gender data state updated, should trigger re-render');
        console.log('🔧 DEBUG: syncCompleted flag set to true');
        console.log('🔧 DEBUG: ================================');
      }
    }
    
    // FORCE TEST for age data - If we see specific patterns, force the correct data
    if (text.includes('18-44') && text.includes('core') && text.includes('45-60') && text.includes('secondary')) {
      console.log('🔧 DEBUG: FORCE AGE MATCH DETECTED - Setting correct age data directly');
      const forceAgeData = [
        { name: '18-44', value: 70, percentage: '70%', color: '#6B4EFF' },
        { name: '45-60', value: 30, percentage: '30%', color: '#FF6B9D' }
      ];
      setAgeData(forceAgeData);
      setPieChartKey(prev => prev + 1);
      setSyncCompleted(true);
      console.log('🔧 DEBUG: FORCE UPDATED age data:', forceAgeData);
      return;
    }
    
    // Extract age data - handle both percentage format and core/secondary format
    const ageMatch = /([0-9]+(?:\.[0-9]+)?)%\s*(?:age|years?)\s*([0-9]+)-([0-9]+)/.exec(text);
    // More flexible patterns for core/secondary format
    const coreMatch = /([0-9]+)-([0-9]+)\s*(?:core|{core}|\(core\))/i.exec(text);
    const secondaryMatch = /([0-9]+)-([0-9]+)\s*(?:secondary|{secondary}|\(secondary\))/i.exec(text);
    
    console.log('🔧 DEBUG: Age regex patterns tested on text:', text);
    console.log('🔧 DEBUG: Core regex pattern: /([0-9]+)-([0-9]+)\\s*core/i');
    console.log('🔧 DEBUG: Secondary regex pattern: /([0-9]+)-([0-9]+)\\s*secondary/i');
    
    console.log('🔧 DEBUG: Age regex matches:', { ageMatch, coreMatch, secondaryMatch });
    console.log('🔧 DEBUG: Age text being searched:', text);
    if (coreMatch) console.log('🔧 DEBUG: Core match found:', coreMatch[0], 'Start:', coreMatch[1], 'End:', coreMatch[2]);
    if (secondaryMatch) console.log('🔧 DEBUG: Secondary match found:', secondaryMatch[0], 'Start:', secondaryMatch[1], 'End:', secondaryMatch[2]);
    
    if (coreMatch && secondaryMatch) {
      console.log('🔧 DEBUG: Found core/secondary format in Product Snapshot');
      const coreStart = parseInt(coreMatch[1]);
      const coreEnd = parseInt(coreMatch[2]);
      const secondaryStart = parseInt(secondaryMatch[1]);
      const secondaryEnd = parseInt(secondaryMatch[2]);
      
      // Collapse to two bands 22–44 (Primary) and 45–55 (Secondary)
      const primaryLabel = `${coreStart}-${coreEnd}`;
      const secondaryLabel = `${secondaryStart}-${secondaryEnd}`;
      setAgeData([
        { name: primaryLabel === '22-44' ? '22-44' : primaryLabel, value: 70, percentage: '', color: '#3B82F6' },
        { name: secondaryLabel === '45-55' ? '45-55' : secondaryLabel, value: 30, percentage: '', color: '#10B981' }
      ]);
      setPieChartKey(prev => {
        const newKey = prev + 1;
        console.log('🔧 DEBUG: Age pie chart key incremented from', prev, 'to', newKey);
        return newKey;
      });
      setSyncCompleted(true);
      console.log('🔧 DEBUG: ===== AGE DATA UPDATED =====');
      console.log('🔧 DEBUG: Updated age data from Product Snapshot (core/secondary):', ageData);
      console.log('🔧 DEBUG: Age data details:', ageData.map(item => `${item.name}: ${item.value}%`));
      console.log('🔧 DEBUG: Age data state updated, should trigger re-render');
      console.log('🔧 DEBUG: syncCompleted flag set to true for age data');
      console.log('🔧 DEBUG: =============================');
    } else if (ageMatch) {
      const percentage = parseFloat(ageMatch[1]);
      const minAge = parseInt(ageMatch[2]);
      const maxAge = parseInt(ageMatch[3]);
      const ageRange = `${minAge}-${maxAge}`;
      
      // Update age data if we have a valid range
      if (minAge && maxAge && percentage) {
        const ageData = [
          { name: ageRange, value: percentage, percentage: `${percentage}%`, color: '#6B4EFF' }
        ];
        setAgeData(ageData);
        setPieChartKey(prev => {
          const newKey = prev + 1;
          console.log('🔧 DEBUG: Age pie chart key incremented from', prev, 'to', newKey);
          return newKey;
        });
        setSyncCompleted(true);
        console.log('🔧 DEBUG: Updated age data from Product Snapshot:', ageData);
        console.log('🔧 DEBUG: Age data state updated, should trigger re-render');
        console.log('🔧 DEBUG: syncCompleted flag set to true for age data (else if)');
      }
    }
    
    console.log('🔧 DEBUG: ===== SYNC FUNCTION COMPLETED =====');
    console.log('🔧 DEBUG: Final genderData:', genderData);
    console.log('🔧 DEBUG: Final ageData:', ageData);
    console.log('🔧 DEBUG: ===================================');
  };

  // Enhanced resize function that also handles content changes
  const resizeTextarea = (textarea: HTMLTextAreaElement) => {
    if (!textarea) return;
    
    // Reset all height constraints
    textarea.style.height = 'auto';
    textarea.style.minHeight = 'auto';
    textarea.style.maxHeight = 'none';
    textarea.style.overflow = 'hidden';
    
    // Force a reflow to get accurate measurements
    textarea.offsetHeight;
    
    // Get the natural content height
    const scrollHeight = textarea.scrollHeight;
    
    // Calculate the required height - use a smaller minimum for Project Overview fields
    const isProjectOverview = textarea.closest('[data-step="project-overview"]') !== null;
    
    // Skip resizing for Project Overview fields as they have fixed heights
    if (isProjectOverview) {
      return;
    }
    
    const minHeight = 110;
    const requiredHeight = Math.max(scrollHeight, minHeight);
    
    // Set the final height
    textarea.style.height = requiredHeight + 'px';
    textarea.style.minHeight = minHeight + 'px';
    textarea.style.maxHeight = 'none';
    textarea.style.overflow = 'hidden';
    textarea.style.resize = 'none';
    
    // Force another reflow
    textarea.offsetHeight;
  };

  // Force resize all textareas function
  const forceResizeAllTextareas = () => {
    const textareas = document.querySelectorAll('textarea');
    textareas.forEach((textarea) => {
      // Skip Project Overview textareas as they have fixed heights
      const isProjectOverview = textarea.closest('[data-step="project-overview"]') !== null;
      if (!isProjectOverview) {
        resizeTextarea(textarea as HTMLTextAreaElement);
      }
    });
    console.log(`Resized ${textareas.length} textareas (skipped Project Overview)`);
  };

  // Aggressive resize function that runs multiple times
  const aggressiveResizeAllTextareas = () => {
    // Only resize if user is not actively editing to prevent glitching
    if (isUserEditing) {
      console.log('Skipping aggressive resize - user is actively editing');
      return;
    }
    
    const textareas = document.querySelectorAll('textarea');
    textareas.forEach((textarea) => {
      // Skip Project Overview textareas as they have fixed heights
      const isProjectOverview = textarea.closest('[data-step="project-overview"]') !== null;
      if (!isProjectOverview) {
        // Only resize once to prevent glitching
        resizeTextarea(textarea as HTMLTextAreaElement);
      }
    });
    console.log(`Resized ${textareas.length} textareas (skipped Project Overview)`);
  };

  // Make function available globally for debugging
  (window as any).resizeTextareas = forceResizeAllTextareas;

  const [showRegeneratePopup, setShowRegeneratePopup] = useState(false);
  const [currentFieldKey, setCurrentFieldKey] = useState<string | null>(null);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [isContentReady, setIsContentReady] = useState(false);
  // removed: lastFocusedValue (unused after fixing preview logic)
  const [lastFocusedLabel, setLastFocusedLabel] = useState<string>('');
  // removed: lockedFields (unused)
  const [isUserEditing, setIsUserEditing] = useState(false);
  const [manuallyAppliedContent] = useState<Set<string>>(new Set());
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  // Auto-resize all textareas when content is loaded
  useEffect(() => {
    if (isContentReady) {
      // Use multiple timeouts to ensure resize happens after DOM updates
      setTimeout(forceResizeAllTextareas, 0);
      setTimeout(forceResizeAllTextareas, 100);
      setTimeout(forceResizeAllTextareas, 300);
    }
  }, [isContentReady]);

  // Auto-resize Project Overview textareas when step becomes active
  useEffect(() => {
    if (activeStep === 0) { // Project Overview is step 0
      setTimeout(() => {
        const projectOverviewTextareas = document.querySelectorAll('[data-step="project-overview"] textarea');
        projectOverviewTextareas.forEach((textarea) => {
          resizeTextarea(textarea as HTMLTextAreaElement);
        });
      }, 100);
    }
  }, [activeStep]);

  const [currentSection, setCurrentSection] = useState('Target Audience');
  // Inline regeneration state is optional; remove if unused
  // const inlineRegenerate = useRegenerateSection();
  
  // Save creative brief hook
  const saveCreativeBrief = useSaveCreativeBrief();

  // Open popup for regenerate with optional feedback prompt
  const openRegeneratePopup = (sectionName: string, fieldKey?: string) => {
    setCurrentSection(sectionName);
    setCurrentFieldKey(fieldKey || null);
    setShowRegeneratePopup(true);
  };

  // Open popup for history with field context
  const openHistoryPopup = (sectionName: string, fieldKey?: string) => {
    setCurrentSection(sectionName);
    setCurrentFieldKey(fieldKey || null);
    setShowHistoryPopup(true);
  };

  // Apply content to a specific field - unified function for both regenerate and history
  const applyContentToField = (sectionName: string, fieldKey: string, content: string) => {
    console.log(`🔧 DEBUG: Applying content to field ${fieldKey} in section ${sectionName}`);
    console.log(`🔧 DEBUG: Content: ${content.substring(0, 100)}...`);
    console.log(`🔧 DEBUG: Content length: ${content.length}`);
    
    // Apply content based on section and field
    switch (sectionName) {
      case 'Product Snapshot':
        switch (fieldKey) {
          case 'productName': 
            console.log(`🔧 DEBUG: Setting productName to: ${content.substring(0, 50)}...`);
            setProductName(content); 
            break;
          case 'productDescription': 
            console.log(`🔧 DEBUG: Setting productDescription to: ${content.substring(0, 50)}...`);
            setProductDescription(content); 
            break;
          case 'problemSolved': 
            console.log(`🔧 DEBUG: Setting problemSolved to: ${content.substring(0, 50)}...`);
            setProblemSolved(content); 
            break;
          case 'targetAudience': 
            console.log(`🔧 DEBUG: Setting targetAudience2 to: ${content.substring(0, 50)}...`);
            setTargetAudience2(content);
            break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Target Customer Deep Dive':
        switch (fieldKey) {
          case 'location': setLocation(content); break;
          case 'income': setIncome(content); break;
          case 'profession': setProfession(content); break;
          case 'lifeStage': setLifeStage(content); break;
          case 'painPoints': setPainPoints(content); break;
          case 'shoppingBehavior': setShoppingBehavior(content); break;
          case 'demographics':
            setDemographics(content);
            // Sync charts strictly from Deep Dive demographics
            syncProductSnapshotToTargetCustomer(content);
            break;
          case 'psychographics':
            setPsychographics(content);
            break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Brand Voice & Tone':
        switch (fieldKey) {
          case 'toneOfVoice': setToneOfVoice(content); break;
          case 'toneOfVoice2': setToneOfVoice2(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'USP(Unique SellingPropositions)':
        switch (fieldKey) {
          case 'uniqueSellingPoints': setUniqueSellingPoints(content); break;
          case 'functionalBenefits': setFunctionalBenefits(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case '5-Second Wow Factor':
        switch (fieldKey) {
          case 'painPoints': setPainPoints(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Key Features (With Context)':
        switch (fieldKey) {
          case 'keyFeatures': 
            console.log(`🔧 DEBUG: Setting keyFeatures to: ${content.substring(0, 50)}...`);
            setKeyFeatures(content); 
            break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Top 6 Selling Points (With Strategic Justification)':
        switch (fieldKey) {
          case 'functionalBenefits': 
            console.log(`🔧 DEBUG: Setting top6SellingPoints to: ${content.substring(0, 50)}...`);
            setTop6SellingPoints(content); 
            break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Competitive Landscape':
        switch (fieldKey) {
          case 'competitor1': setCompetitor1(content); break;
          case 'competitor2': setCompetitor2(content); break;
          case 'competitor3': setCompetitor3(content); break;
          case 'competitorComparison': setCompetitorComparison(content); break;
          case 'amazonDifferentiators': setAmazonDifferentiators(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Search & Keywords Strategy':
        switch (fieldKey) {
          case 'searchKeywords': setSearchKeywords(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Brand Story, Values & Purpose':
        switch (fieldKey) {
          case 'brandStory': setBrandStory(content); break;
          case 'brandStory2': setBrandStory2(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Design Direction':
        switch (fieldKey) {
          case 'designDirection': setDesignDirection(content); break;
          case 'designDirection2': setDesignDirection2(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Final Notes & Strategic Callouts':
        switch (fieldKey) {
          case 'finalNotes': setFinalNotes(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Current Listing Challenges':
        switch (fieldKey) {
          case 'currentChallenges': setCurrentChallenges(content); break;
          case 'conversionLoss': setConversionLoss(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Barriers To Purchase':
        switch (fieldKey) {
          case 'barriersToPurchase': setBarriersToPurchase(content); break;
          case 'conversionLoss': setConversionLoss(content); break;
          default: console.warn(`Unknown field key: ${fieldKey}`); break;
        }
        break;
      case 'Project Overview':
        setProjectName(content);
        break;
      default:
        console.warn(`Unknown section: ${sectionName}`);
        break;
    }
    
    // Track that this content was manually applied
    manuallyAppliedContent.add(`${sectionName}.${fieldKey}`);
  };

  // Removed unused helper getSectionNameFromLabel

  // Fetch project data and creative brief
  // Disable polling when user is actively working with content to prevent reloads
  const isUserActive = showRegeneratePopup || showHistoryPopup || isUserEditing || manuallyAppliedContent.size > 0;
  
  console.log('🔧 DEBUG: isUserActive:', isUserActive);
  
  const { data: dashboardData, isLoading, error } = useDashboardData(projectId ? Number(projectId) : null, { 
    disablePolling: isUserActive 
  });
  const { data: historyResponse } = useCreativeBriefHistory(projectId || null);

  // Additional effect to resize textareas when content changes
  useEffect(() => {
    if (dashboardData) {
      // Use multiple timeouts to ensure resize happens after DOM updates
      setTimeout(forceResizeAllTextareas, 0);
      setTimeout(forceResizeAllTextareas, 100);
      setTimeout(forceResizeAllTextareas, 300);
      setTimeout(forceResizeAllTextareas, 500);
    }
  }, [dashboardData, isContentReady, forceUpdate]);

  // Resize textareas when active step changes
  useEffect(() => {
    setTimeout(forceResizeAllTextareas, 0);
    setTimeout(forceResizeAllTextareas, 100);
  }, [activeStep]);

  // Resize textareas when content is updated via regeneration
  useEffect(() => {
    if (forceUpdate > 0) {
      setTimeout(forceResizeAllTextareas, 0);
      setTimeout(forceResizeAllTextareas, 100);
      setTimeout(forceResizeAllTextareas, 300);
    }
  }, [forceUpdate]);

  // Additional effect to ensure textareas are resized after any content change
  useEffect(() => {
    const timer = setTimeout(() => {
      aggressiveResizeAllTextareas();
    }, 100);
    return () => clearTimeout(timer);
  }, [dashboardData, isContentReady, activeStep]);

  // Use MutationObserver to watch for textarea content changes
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          const textareas = document.querySelectorAll('textarea');
          textareas.forEach((textarea) => {
            resizeTextarea(textarea as HTMLTextAreaElement);
          });
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['value']
    });

    return () => observer.disconnect();
  }, []);

  // Additional effect to resize textareas when they receive focus or content changes
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      if (e.target instanceof HTMLTextAreaElement) {
        setTimeout(() => {
          resizeTextarea(e.target as HTMLTextAreaElement);
        }, 10);
        try {
          const el = e.target as HTMLTextAreaElement;
          // removed: setLastFocusedValue (no longer needed)
          let labelText = '';
          let node: HTMLElement | null = el.parentElement as HTMLElement | null;
          for (let i = 0; i < 3 && node; i++) {
            const label = node.querySelector('label');
            if (label && label.textContent) { labelText = label.textContent; break; }
            node = node.parentElement;
          }
          if (labelText) setLastFocusedLabel(labelText.trim());
        } catch {}
      }
    };

    const handleInput = (e: Event) => {
      if (e.target instanceof HTMLTextAreaElement) {
        resizeTextarea(e.target as HTMLTextAreaElement);
      }
    };

    document.addEventListener('focus', handleFocus, true);
    document.addEventListener('input', handleInput, true);

    return () => {
      document.removeEventListener('focus', handleFocus, true);
      document.removeEventListener('input', handleInput, true);
    };
  }, []);

  // Aggressive resize on component mount and content changes
  useEffect(() => {
    const resizeAll = () => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach((textarea) => {
        const element = textarea as HTMLTextAreaElement;
        // Force immediate resize
        element.style.setProperty('height', 'auto', 'important');
        element.style.setProperty('height', element.scrollHeight + 'px', 'important');
        element.style.setProperty('overflow', 'hidden', 'important');
      });
    };

    // Run immediately
    resizeAll();
    
    // Run after a short delay to ensure DOM is ready
    const timer1 = setTimeout(resizeAll, 100);
    const timer2 = setTimeout(resizeAll, 300);
    const timer3 = setTimeout(resizeAll, 500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isContentReady, dashboardData, activeStep]);

  // Helper: extract brief JSON regardless of backend shape
  const extractBrief = (data: any): any | null => {
    if (!data) return null;
    const cb = (data as any).creative_brief;
    if (cb && cb.brief_json) return cb.brief_json;
    if (cb && typeof cb === 'object' && Object.keys(cb).length > 0) return cb;
    // sometimes under reports
    const rep = (data as any).reports;
    if (rep && rep.creative_brief && rep.creative_brief.content) return rep.creative_brief.content;
    return null;
  };

  // Also extract from history API
  const extractBriefFromHistory = (hist: any): any | null => {
    if (!hist) return null;
    // Try common shapes: {history: [...]}, array, or object with entries
    const list = Array.isArray(hist) ? hist : (hist.history || hist.items || hist.data || []);
    if (!Array.isArray(list) || list.length === 0) return null;
    const latest = list.reduce((a: any, b: any) => (new Date(a.created_at || 0) > new Date(b.created_at || 0) ? a : b));
    if (!latest) return null;
    if (latest.brief_json) return latest.brief_json;
    if (latest.content) return latest.content;
    if (latest.data) return latest.data;
    return null;
  };

  // Normalize history list regardless of backend shape
  const extractHistoryList = (hist: any): any[] => {
    if (!hist) return [];
    if (Array.isArray(hist)) return hist;
    if (Array.isArray((hist as any).history)) return (hist as any).history;
    if (Array.isArray((hist as any).items)) return (hist as any).items;
    if (Array.isArray((hist as any).data)) return (hist as any).data;
    if (typeof hist === 'object') return Object.values(hist as any);
    return [];
  };


  // Debug activeStep changes
  useEffect(() => {
    console.log('activeStep changed to:', activeStep);
  }, [activeStep]);
  
  // State for editable text fields - will be populated from API data
  const [projectName, setProjectName] = useState("");
  const [productName, setProductName] = useState("");
  const [brandName, setBrandName] = useState("");
  const [websiteLink, setWebsiteLink] = useState("");
  const [amazonListing, setAmazonListing] = useState("");
  const [instagram, setInstagram] = useState("");
  const [productDescription, setProductDescription] = useState("");

  const [location, setLocation] = useState("");
  const [income, setIncome] = useState("");
  const [profession, setProfession] = useState("");
  
  // Additional state for other text fields
  const [problemSolved, setProblemSolved] = useState("");
  const [targetAudience2, setTargetAudience2] = useState("");
  const [currentChallenges, setCurrentChallenges] = useState("");
  const [conversionLoss, setConversionLoss] = useState("");
  const [barriersToPurchase, setBarriersToPurchase] = useState("");
  const [lifeStage, setLifeStage] = useState("");
  const [painPoints, setPainPoints] = useState("");
  const [shoppingBehavior, setShoppingBehavior] = useState("");
  const [demographics, setDemographics] = useState("");
  const [psychographics, setPsychographics] = useState("");
  
  // Additional state for creative brief sections
  const [keyFeatures, setKeyFeatures] = useState("");
  const [functionalBenefits, setFunctionalBenefits] = useState("");
  const [top6SellingPoints, setTop6SellingPoints] = useState("");
  const [uniqueSellingPoints, setUniqueSellingPoints] = useState("");
  const [toneOfVoice, setToneOfVoice] = useState("");
  
  // Additional state for remaining steps
  const [searchKeywords, setSearchKeywords] = useState("");
  const [brandStory, setBrandStory] = useState("");
  const [brandValues] = useState("");
  const [designDirection, setDesignDirection] = useState("");
  const [finalNotes, setFinalNotes] = useState("");
  const [competitor1, setCompetitor1] = useState("");
  const [competitor2, setCompetitor2] = useState("");
  const [competitor3, setCompetitor3] = useState("");
  const [competitorComparison, setCompetitorComparison] = useState("");
  const [amazonDifferentiators, setAmazonDifferentiators] = useState("");
  
  // Additional state for second questions in sections
  const [toneOfVoice2, setToneOfVoice2] = useState("");
  const [brandStory2, setBrandStory2] = useState("");
  const [designDirection2, setDesignDirection2] = useState("");
  
  // State for demographic graphs with proper percentage formatting
  const [genderData, setGenderData] = useState([
    { name: 'Male', value: 0, percentage: '0%', color: '#6B4EFF' },
    { name: 'Female', value: 0, percentage: '0%', color: '#FF6B9D' },
    { name: 'Other', value: 0, percentage: '0%', color: '#4ECDC4' }
  ]);
  const [ageData, setAgeData] = useState([
    { name: '22-44', value: 0, percentage: '', color: '#3B82F6' },
    { name: '45-55', value: 0, percentage: '', color: '#10B981' }
  ]);
  
  // Force re-render key for pie charts
  const [pieChartKey, setPieChartKey] = useState(0);
  
  // Flag to prevent data loading from overriding sync results
  const [syncCompleted, setSyncCompleted] = useState(false);
  
  // Debug: Log pie chart key changes
  useEffect(() => {
    console.log('🔧 DEBUG: pieChartKey changed to:', pieChartKey);
  }, [pieChartKey]);

  // Resize textareas when any content state changes (but only if user is not editing)
  useEffect(() => {
    if (isUserEditing) {
      console.log('Skipping resize effect - user is actively editing');
      return;
    }
    
    const timer = setTimeout(() => {
      aggressiveResizeAllTextareas();
    }, 200); // Increased delay to reduce frequency
    return () => clearTimeout(timer);
  }, [
    projectName, brandName, websiteLink, amazonListing, instagram,
    productDescription, problemSolved, targetAudience2, currentChallenges,
    conversionLoss, lifeStage, painPoints, shoppingBehavior, toneOfVoice,
    uniqueSellingPoints, keyFeatures, searchKeywords, brandStory, designDirection, finalNotes,
    brandValues, functionalBenefits, top6SellingPoints, competitor1, toneOfVoice2, brandStory2, designDirection2,
    isUserEditing // Add isUserEditing to dependencies
  ]);

  // Additional aggressive resize for content changes
  useEffect(() => {
    const resizeAllTextareas = () => {
      const textareas = document.querySelectorAll('textarea');
      textareas.forEach((textarea) => {
        const element = textarea as HTMLTextAreaElement;
        // Force resize with !important
        element.style.setProperty('height', 'auto', 'important');
        element.style.setProperty('height', element.scrollHeight + 'px', 'important');
        element.style.setProperty('overflow', 'hidden', 'important');
        element.style.setProperty('min-height', 'auto', 'important');
        element.style.setProperty('max-height', 'none', 'important');
      });
    };

    // Only run if user is not actively editing
    if (!isUserEditing) {
      // Run only once with a delay to avoid interfering with user input
      setTimeout(resizeAllTextareas, 200);
    }
  }, [
    projectName, brandName, websiteLink, amazonListing, instagram,
    productDescription, problemSolved, targetAudience2, currentChallenges,
    conversionLoss, lifeStage, painPoints, shoppingBehavior, toneOfVoice,
    uniqueSellingPoints, keyFeatures, searchKeywords, brandStory, designDirection, finalNotes,
    brandValues, functionalBenefits, top6SellingPoints, competitor1, toneOfVoice2, brandStory2, designDirection2,
    isUserEditing
  ]);

  // Do not auto-sync charts from Product Snapshot targetAudience; charts follow Deep Dive only

  // Debug: Monitor genderData changes
  useEffect(() => {
    console.log('🔧 DEBUG: genderData state changed to:', genderData);
    console.log('🔧 DEBUG: genderData values:', genderData.map(item => `${item.name}: ${item.value}%`));
  }, [genderData]);

  // Debug: Monitor ageData changes
  useEffect(() => {
    console.log('🔧 DEBUG: ageData state changed to:', ageData);
    console.log('🔧 DEBUG: ageData values:', ageData.map(item => `${item.name}: ${item.value}%`));
  }, [ageData]);

  // Remove mount-time test overrides; charts should reflect actual Deep Dive data only


  // Load data from API when available
  useEffect(() => {
    // Reset sync flag when new data is loaded
    setSyncCompleted(false);
    
    if ((dashboardData as any)?.project) {
      const project = (dashboardData as any).project;
      setProjectName(safeExtractText(project.project_name));
      setBrandName(safeExtractText(project.brand_name));
      setWebsiteLink(safeExtractText(project.website_link));
      setAmazonListing(safeExtractText(project.amazon_listing_link));
      setInstagram(safeExtractText(project.instagram_link));
      setProductDescription(safeExtractText(project.product_info));
    }
    
    const extracted = extractBrief(dashboardData as any) || extractBriefFromHistory(historyResponse as any);
    if (extracted) {
      const brief = extracted;
      // Mark content ready as soon as brief exists to avoid indefinite loader
      setIsContentReady(true);

      // Map the new 15-section backend structure to frontend state variables
      try {
        // Parse brief if it's a string
        let briefData = brief;
        if (typeof brief === 'string') {
          briefData = JSON.parse(brief);
        }
        
        console.log("Creative brief data loaded:", briefData);
        
        // Map each section to the corresponding state variable
        // Step 1: Project Overview
        // Do not overwrite projectName here; keep from project data
        // If needed, you can display overview separately from projectName input
        
        // Step 2: Product Snapshot
        if (briefData["Product Snapshot"]) {
          try {
            const productSnapshotData = briefData["Product Snapshot"];
            
            // Check if it's a nested object with question fields (new format)
            if (typeof productSnapshotData === 'object' && productSnapshotData !== null) {
              console.log('🔧 DEBUG: Product Snapshot is nested object with keys:', Object.keys(productSnapshotData));
              console.log('🔧 DEBUG: Product Snapshot data:', productSnapshotData);
              
              if (productSnapshotData.productName) {
                const productNameValue = safeExtractText(productSnapshotData.productName);
                console.log('🔧 DEBUG: Setting productName to:', productNameValue);
                setProductName(productNameValue);
              }
              if (productSnapshotData.whatItDoes) {
                const whatItDoesValue = safeExtractText(productSnapshotData.whatItDoes);
                console.log('🔧 DEBUG: Setting productDescription to:', whatItDoesValue);
                setProductDescription(whatItDoesValue);
              }
              if (productSnapshotData.problemSolved) {
                const problemSolvedValue = safeExtractText(productSnapshotData.problemSolved);
                console.log('🔧 DEBUG: Setting problemSolved to:', problemSolvedValue);
                setProblemSolved(problemSolvedValue);
              }
              if (productSnapshotData.targetAudience) {
                const targetAudienceValue = safeExtractText(productSnapshotData.targetAudience);
                console.log('🔧 DEBUG: Setting targetAudience2 to:', targetAudienceValue);
                setTargetAudience2(targetAudienceValue);
              }
            if (productSnapshotData.genderBreakdown) {
              // Parse gender breakdown and update charts
              const genderText = safeExtractText(productSnapshotData.genderBreakdown);
              console.log('🔧 DEBUG: Gender breakdown from Product Snapshot:', genderText);
              // This will be processed by the sync function
            }
            if (productSnapshotData.incomeLevel) {
              const incomeText = safeExtractText(productSnapshotData.incomeLevel);
              console.log('🔧 DEBUG: Income level from Product Snapshot:', incomeText);
            }
            if (productSnapshotData.profession) {
              const professionText = safeExtractText(productSnapshotData.profession);
              console.log('🔧 DEBUG: Profession from Product Snapshot:', professionText);
            }
          } else {
            // Fallback: Use string content (old format)
            console.log('🔧 DEBUG: Product Snapshot is string (old format)');
            const productSnapshotText = safeExtractText(productSnapshotData);
            
            // Extract product name from the beginning of the text
            const productName = productSnapshotText.split('.')[0] || productSnapshotText.split(',')[0] || productSnapshotText;
            setProductName(productName);
            
            // Use the full text for product description
            setProductDescription(productSnapshotText);
            
            // Try to extract problem-solving information from the text
            const problemKeywords = ['solves', 'fixes', 'reduces', 'prevents', 'treats', 'helps', 'addresses', 'improves'];
            const problemSentences = productSnapshotText.split('.').filter(sentence => 
              problemKeywords.some(keyword => sentence.toLowerCase().includes(keyword))
            );
            
            if (problemSentences.length > 0) {
              const problemText = problemSentences.slice(0, 2).join('. ').trim();
              if (problemText) {
                setProblemSolved(problemText + '.');
              }
            }
            
            // Try to extract target audience information
            const audienceKeywords = ['for', 'designed for', 'target', 'users', 'customers', 'consumers'];
            const audienceSentences = productSnapshotText.split('.').filter(sentence => 
              audienceKeywords.some(keyword => sentence.toLowerCase().includes(keyword))
            );
            
            if (audienceSentences.length > 0) {
              const audienceText = audienceSentences.slice(0, 2).join('. ').trim();
              if (audienceText) {
                setTargetAudience2(audienceText + '.');
              }
            }
            
            console.log('🔧 DEBUG: Extracted info from old format - some fields may be populated');
          }
          } catch (error) {
            console.error('🔧 DEBUG: Error in Product Snapshot processing:', error);
            // Fallback: try to extract basic info from string format
            if (typeof briefData["Product Snapshot"] === 'string') {
              const productSnapshotText = safeExtractText(briefData["Product Snapshot"]);
              setProductName(productSnapshotText.split('.')[0] || productSnapshotText);
              setProductDescription(productSnapshotText);
            }
          }
        }
        
        // Step 3: Current Listing Challenges (load first to set conversionLoss)
        if (briefData["Current Listing Challenges"]) {
          const challengesData = briefData["Current Listing Challenges"];
          
          // Handle nested object structure (new format)
          if (typeof challengesData === 'object' && challengesData !== null) {
            if (challengesData.currentChallenges) {
              setCurrentChallenges(safeExtractText(challengesData.currentChallenges));
            }
            if (challengesData.conversionLoss) {
              setConversionLoss(safeExtractText(challengesData.conversionLoss));
            }
          } else {
            // Handle string format (old format)
            setCurrentChallenges(safeExtractText(challengesData));
          }
        }
        
        // Set Barriers To Purchase content for conversionLoss (only if Current Listing Challenges didn't set it)
        if (briefData["Barriers To Purchase"] && !briefData["Current Listing Challenges"]) {
          const barriersText = safeExtractText(briefData["Barriers To Purchase"]);
          // Full barriers text in conversionLoss section
          setConversionLoss(barriersText);
          // Only derive problemSolved if Product Snapshot didn't already set it
          if (!briefData["Product Snapshot"] || (typeof briefData["Product Snapshot"] === 'object' && !briefData["Product Snapshot"].problemSolved)) {
            const firstBarrier = barriersText.split('—')[0] || barriersText.split('–')[0] || barriersText.split('\n')[0];
            console.log('🔧 DEBUG: Setting problemSolved from Barriers To Purchase:', (firstBarrier || '').trim());
            setProblemSolved((firstBarrier || '').trim());
          } else {
            console.log('🔧 DEBUG: NOT overwriting problemSolved from Barriers To Purchase - Product Snapshot already set it');
          }
        }
        
        // Step 4: Target Customer Deep Dive
        if (briefData["Target Customer Deep Dive"]) {
          console.log('🔧 DEBUG: Data loading - Processing Target Customer Deep Dive data');
          const targetAudience = briefData["Target Customer Deep Dive"];
          if (typeof targetAudience === 'object' && targetAudience !== null) {
            const loc = safeExtractText(targetAudience.Location || targetAudience.location);
            const inc = safeExtractText(targetAudience.Income || targetAudience.income);
            const prof = safeExtractText(targetAudience.Profession || targetAudience.profession);
            const life = safeExtractText(targetAudience["Life Stage"] || targetAudience.lifeStage);
            const gender = safeExtractText(targetAudience.Gender || targetAudience.gender);
            const age = safeExtractText(targetAudience.Age || targetAudience.age);
            const tags = Array.isArray(targetAudience["Identity Tags"]) ? targetAudience["Identity Tags"].map((t: any) => safeExtractText(t)).filter(Boolean).join(', ') : safeExtractText((targetAudience as any).identityTags);
            const shop = safeExtractText(targetAudience["How do they shop on Amazon?"] || targetAudience.shoppingBehavior || (targetAudience as any).shoppingBehavior || '');
            const demo = safeExtractText(targetAudience.Demographics || targetAudience.demographics || '');
            const psycho = safeExtractText(targetAudience.Psychographics || targetAudience.psychographics || '');
            const pain = safeExtractText(targetAudience["Pain Points"] || targetAudience.painPoints || '');

            setLocation(loc);
            setIncome(inc);
            setProfession(prof);
            setLifeStage(life);
            setShoppingBehavior(shop);
            setDemographics(demo);
            setPsychographics(psycho);
            setPainPoints(pain);
            // Only populate the free-text summary if Product Snapshot didn't already set targetAudience
            const summaryParts = [gender, age, inc, life, prof, loc, tags].filter(Boolean);
            if (!briefData["Product Snapshot"] || (typeof briefData["Product Snapshot"] === 'object' && !briefData["Product Snapshot"].targetAudience)) {
              setTargetAudience2(summaryParts.join(' | '));
            }
            
            console.log('🔧 DEBUG: Synced Target Customer Deep Dive data to Product Snapshot:', {
              gender, age, income: inc, lifeStage: life, profession: prof, location: loc,
              summary: summaryParts ? summaryParts.join(' | ') : 'No summary parts'
            });
            
            // Avoid syncing charts from summary; charts should use explicit Gender/Age/Demographics fields
            if (summaryParts && summaryParts.length > 0) {
              console.log('🔧 DEBUG: Data loading - skipping summary-based chart sync to avoid conflicts');
            }

            // Parse Gender into chart data if percentages are present
            if (gender && !syncCompleted) {
              console.log('🔧 DEBUG: Data loading - parsing gender:', gender);
              const lower = gender.toLowerCase();
              // Use the same improved regex patterns as the sync function
              const maleMatch = /(?:male\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*male|male\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*male)/i.exec(lower);
              const femaleMatch = /(?:female\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*female|female\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*female)/i.exec(lower);
              const otherMatch = /(?:other\s*([0-9]+(?:\.[0-9]+)?)%|([0-9]+(?:\.[0-9]+)?)%\s*(?:other|non-binary|nonbinary|nb|prefer not to say)|other\s*([0-9]+(?:\.[0-9]+)?)|([0-9]+(?:\.[0-9]+)?)\s*(?:other|non-binary|nonbinary|nb|prefer not to say))/i.exec(lower);
              console.log('🔧 DEBUG: Data loading - gender regex matches:', { maleMatch, femaleMatch, otherMatch });
              let male = maleMatch ? parseFloat(maleMatch[1] || maleMatch[2] || maleMatch[3] || maleMatch[4]) : NaN;
              let female = femaleMatch ? parseFloat(femaleMatch[1] || femaleMatch[2] || femaleMatch[3] || femaleMatch[4]) : NaN;
              let other = otherMatch ? parseFloat(otherMatch[1] || otherMatch[2] || otherMatch[3] || otherMatch[4]) : NaN;

              // If only two present, infer the third to make 100
              const known = [male, female, other].filter(v => !isNaN(v));
              if (known.length >= 2) {
                const sumKnown = (isNaN(male) ? 0 : male) + (isNaN(female) ? 0 : female) + (isNaN(other) ? 0 : other);
                const missingIndex = [male, female, other].findIndex(v => isNaN(v));
                const remaining = Math.max(0, 100 - sumKnown);
                if (missingIndex === 0) male = remaining;
                if (missingIndex === 1) female = remaining;
                if (missingIndex === 2) other = remaining;
              }

              if (!isNaN(male) || !isNaN(female) || !isNaN(other)) {
                const m = isNaN(male) ? 0 : male;
                const f = isNaN(female) ? 0 : female;
                const o = isNaN(other) ? 0 : other;
                const total = m + f + o || 1;
                const norm = (v: number) => Math.max(0, Math.min(100, (v / total) * 100));
                const gd = [
                  { name: 'Male', value: parseFloat(norm(m).toFixed(1)), percentage: `${norm(m).toFixed(1)}%`, color: '#6B4EFF' },
                  { name: 'Female', value: parseFloat(norm(f).toFixed(1)), percentage: `${norm(f).toFixed(1)}%`, color: '#FF6B9D' },
                  { name: 'Other', value: parseFloat(norm(o).toFixed(1)), percentage: `${norm(o).toFixed(1)}%`, color: '#4ECDC4' }
                ];
                setGenderData(gd);
              } else {
                // Fallback: infer from keywords when no explicit percentages are present
                console.log('🔧 DEBUG: Data loading - using fallback logic (50/50)');
                const mentionsMale = /(\bmale\b|\bmen\b|\bboys\b|\bman\b)/.test(lower);
                const mentionsFemale = /(\bfemale\b|\bwomen\b|\bgirls\b|\bwoman\b|\bmoms\b)/.test(lower);
                const mentionsOther = /(non-binary|nonbinary|nb|prefer not to say|other)/.test(lower);
                let m = 50, f = 50, o = 0;
                if (mentionsMale && mentionsFemale) { m = 50; f = 50; o = mentionsOther ? 5 : 0; }
                else if (mentionsFemale && !mentionsMale) { f = 80; m = 20; o = mentionsOther ? 5 : 0; }
                else if (mentionsMale && !mentionsFemale) { m = 80; f = 20; o = mentionsOther ? 5 : 0; }
                else { m = 50; f = 50; o = 0; }
                const total2 = m + f + o || 1;
                const norm2 = (v: number) => Math.max(0, Math.min(100, (v / total2) * 100));
                const fallbackData = [
                  { name: 'Male', value: parseFloat(norm2(m).toFixed(1)), percentage: `${norm2(m).toFixed(1)}%`, color: '#6B4EFF' },
                  { name: 'Female', value: parseFloat(norm2(f).toFixed(1)), percentage: `${norm2(f).toFixed(1)}%`, color: '#FF6B9D' },
                  { name: 'Other', value: parseFloat(norm2(o).toFixed(1)), percentage: `${norm2(o).toFixed(1)}%`, color: '#4ECDC4' },
                ];
                console.log('🔧 DEBUG: Data loading - setting fallback gender data:', fallbackData);
                setGenderData(fallbackData);
              }
            } else if (gender) {
              console.log('🔧 DEBUG: Data loading - skipping gender parsing (sync already completed)');
            }

            // Parse Age into chart data if ranges/percentages are present
            if (age && !syncCompleted) {
              console.log('🔧 DEBUG: Data loading - parsing age:', age);
              const lower = age.toLowerCase();
              const buckets = {
                '18-24': 0,
                '25-34': 0,
                '35-44': 0,
                '45-54': 0,
                '55+': 0,
              } as Record<string, number>;

              const assign = (label: string, pct: number) => { buckets[label] = Math.max(buckets[label], pct); };

              // First, try to parse "core" and "secondary" format like "18-44 core; 45-54 secondary"
              const coreMatch = /([0-9]+)[-–]([0-9]+)\s*(?:core|{core}|\(core\))/i.exec(lower);
              const secondaryMatch = /([0-9]+)[-–]([0-9]+)\s*(?:secondary|{secondary}|\(secondary\))/i.exec(lower);
              
              if (coreMatch && secondaryMatch) {
                console.log('🔧 DEBUG: Data loading - Found core/secondary format:', { coreMatch, secondaryMatch });
                // Use the exact ranges from the text rather than generic labels
                const coreStart = parseInt(coreMatch[1]);
                const coreEnd = parseInt(coreMatch[2]);
                const secondaryStart = parseInt(secondaryMatch[1]);
                const secondaryEnd = parseInt(secondaryMatch[2]);
                const bandPrimary = { name: `${coreStart}-${coreEnd}`, value: 70, percentage: '', color: '#3B82F6' };
                const bandSecondary = { name: `${secondaryStart}-${secondaryEnd}`, value: 30, percentage: '', color: '#10B981' };
                setAgeData([bandPrimary, bandSecondary]);
                setPieChartKey(prev => prev + 1);
                setSyncCompleted(true);
                console.log('🔧 DEBUG: Data loading - Updated age data from core/secondary:', ageData);
                return; // Skip the rest of the age parsing logic
              } else {
                // Fallback to original parsing logic
                const rx = /(18-24|25-34|35-44|45-54|55\+|55\+|55\s*\+)\s*[:\-]?\s*([0-9]+(?:\.[0-9]+)?)%/g;
                let m: RegExpExecArray | null;
                while ((m = rx.exec(lower)) !== null) {
                  const label = m[1].replace(/\s+/g, '');
                  const key = label.includes('18-24') ? '18-24' : label.includes('25-34') ? '25-34' : label.includes('35-44') ? '35-44' : label.includes('45-54') ? '45-54' : '55+';
                  assign(key, parseFloat(m[2]));
                }
              }

              const values = Object.values(buckets);
              if (values.some(v => v > 0)) {
                const total = values.reduce((a, b) => a + b, 0) || 1;
                const band1 = (buckets['18-24'] + buckets['25-34'] + buckets['35-44']) / total * 100;
                const band2 = (buckets['45-54']) / total * 100;
                setAgeData([
                  { name: '22-44', value: Math.round(band1), percentage: '', color: '#3B82F6' },
                  { name: '45-55', value: Math.round(band2), percentage: '', color: '#10B981' },
                ]);
              } else {
                // Fallback: even split across mentioned ranges (without %), else typical ecommerce distribution
                const mentioned = [
                  /(18\s*-\s*24)/.test(lower) ? '18-24' : null,
                  /(25\s*-\s*34)/.test(lower) ? '25-34' : null,
                  /(35\s*-\s*44)/.test(lower) ? '35-44' : null,
                  /(45\s*-\s*54)/.test(lower) ? '45-54' : null,
                  /(55\s*\+|55\+|55\s*plus)/.test(lower) ? '55+' : null,
                ].filter(Boolean) as string[];
                let dist: Record<string, number> = { '18-24': 0, '25-34': 0, '35-44': 0, '45-54': 0, '55+': 0 };
                if (mentioned.length > 0) {
                  const share = 100 / mentioned.length;
                  mentioned.forEach(k => { dist[k] = share; });
                } else {
                  // Typical distribution fallback
                  dist = { '18-24': 15, '25-34': 35, '35-44': 30, '45-54': 15, '55+': 5 };
                }
                const b1 = dist['18-24'] + dist['25-34'] + dist['35-44'];
                const b2 = dist['45-54'];
                const tot = b1 + b2 || 1;
                setAgeData([
                  { name: '22-44', value: Math.round((b1 / tot) * 100), percentage: '', color: '#3B82F6' },
                  { name: '45-55', value: Math.round((b2 / tot) * 100), percentage: '', color: '#10B981' },
                ]);
              }
            } else if (age) {
              console.log('🔧 DEBUG: Data loading - skipping age parsing (sync already completed)');
            }

            // Derive Amazon shopping behavior from available sections
            // Try multiple possible field names and sources
            const behaviorFromTarget = safeExtractText((targetAudience as any)['Shopping Behavior'] || (targetAudience as any)['Amazon Shopping Behavior'] || (targetAudience as any)['Behavior'] || (targetAudience as any)['Shopping Patterns'] || '');
            
            // Also try to extract from other sections that might contain shopping behavior
            let behaviorFromOtherSections = '';
            if (briefData['Barriers To Purchase']) {
              const barriersText = safeExtractText(briefData['Barriers To Purchase']);
              // Extract shopping-related content from barriers
              const shoppingKeywords = ['shop', 'buy', 'purchase', 'amazon', 'browse', 'scroll', 'search', 'compare', 'review'];
              const sentences = barriersText.split(/[.!?]+/).filter(sentence => 
                shoppingKeywords.some(keyword => sentence.toLowerCase().includes(keyword))
              );
              if (sentences.length > 0) {
                behaviorFromOtherSections = sentences.slice(0, 2).join('. ').trim();
              }
            }
            
            let whyText = '';
            const pts2 = briefData['Top 6 Selling Points (With Strategic Justification)'];
            if (Array.isArray(pts2)) {
              whyText = pts2
                .map((p: any) => (p?.why ? safeExtractText(p.why) : safeExtractText(p)))
                .filter(Boolean)
                .slice(0, 3)
                .join(' | ');
            }
            
            // Only set shopping behavior if we don't already have it from the direct field
            if (!shop) {
              // Combine all possible sources for shopping behavior
              const behaviorCandidates = [behaviorFromTarget, behaviorFromOtherSections, whyText].filter(Boolean);
              console.log('🔧 DEBUG: Shopping behavior candidates:', { behaviorFromTarget, behaviorFromOtherSections, whyText, behaviorCandidates });
              if (behaviorCandidates.length) {
                const finalBehavior = behaviorCandidates.join(' | ');
                setShoppingBehavior(finalBehavior);
                console.log('🔧 DEBUG: Set shopping behavior to:', finalBehavior);
              } else {
                // Fallback: create a generic shopping behavior based on demographics
                const genericBehavior = `Based on the target demographic (${[gender, age, inc, life, prof].filter(Boolean).join(', ')}), customers likely shop on Amazon by browsing product listings, reading reviews, comparing prices, and looking for specific features that match their needs.`;
                setShoppingBehavior(genericBehavior);
                console.log('🔧 DEBUG: Set generic shopping behavior to:', genericBehavior);
              }
            } else {
              console.log('🔧 DEBUG: Shopping behavior already set from direct field:', shop);
            }

            // Populate demographics field (text block) but do not drive charts from summary
            const demographicsData = [gender, age, inc, loc, prof].filter(Boolean);
            if (demographicsData.length) {
              const demographicsText = demographicsData.join(' | ');
              setDemographics(demographicsText);
            }

            // Populate psychographics field with lifestyle and behavioral data
            const psychographicsData = [life, tags, behaviorFromTarget].filter(Boolean);
            if (psychographicsData.length) {
              setPsychographics(psychographicsData.join(' | '));
            }
          } else {
            const targetAudienceText = safeExtractText(targetAudience);
            // Only set targetAudience2 if Product Snapshot didn't already set it
            if (!briefData["Product Snapshot"] || (typeof briefData["Product Snapshot"] === 'object' && !briefData["Product Snapshot"].targetAudience)) {
              setTargetAudience2(targetAudienceText);
            }
            // Also set demographics text; do not sync charts from summary
            setDemographics(targetAudienceText);
            
            // Only try to extract shopping behavior if we don't already have it
            if (!shoppingBehavior) {
              // Try to extract shopping behavior from the string text
              const shoppingKeywords = ['shop', 'buy', 'purchase', 'amazon', 'browse', 'scroll', 'search', 'compare', 'review'];
              const sentences = targetAudienceText.split(/[.!?]+/).filter(sentence => 
                shoppingKeywords.some(keyword => sentence.toLowerCase().includes(keyword))
              );
              if (sentences.length > 0) {
                const shoppingBehaviorText = sentences.slice(0, 2).join('. ').trim();
                setShoppingBehavior(shoppingBehaviorText);
                console.log('🔧 DEBUG: Set shopping behavior from string to:', shoppingBehaviorText);
              }
            } else {
              console.log('🔧 DEBUG: Shopping behavior already set, not overwriting');
            }
          }
        }
        
        // Step 5: Barriers To Purchase
        if (briefData["Barriers To Purchase"]) {
          const barriersData = briefData["Barriers To Purchase"];
          
          // Handle nested object structure (new format)
          if (typeof barriersData === 'object' && barriersData !== null) {
            if (barriersData.conversionLoss) {
              setBarriersToPurchase(safeExtractText(barriersData.conversionLoss));
            }
          } else {
            // Handle string format (old format)
            const barriersText = safeExtractText(barriersData);
            setBarriersToPurchase(barriersText);
          }
          
          // Only set conversionLoss from Barriers To Purchase if Current Listing Challenges didn't set it
          if (!briefData["Current Listing Challenges"]) {
            const barriersText = safeExtractText(barriersData);
            setConversionLoss(barriersText);
          }
        }
        
        // Step 6: Tone of Voice
        if (briefData["Tone of Voice"]) {
          const toneData = briefData["Tone of Voice"];
          
          // Check if it's a nested object with question fields
          if (typeof toneData === 'object' && toneData !== null) {
            if (toneData.toneOfVoice) {
              setToneOfVoice(safeExtractText(toneData.toneOfVoice));
            }
            if (toneData.toneOfVoice2) {
              setToneOfVoice2(safeExtractText(toneData.toneOfVoice2));
            }
          } else {
            // Fallback: Split string content intelligently for two different questions
            const toneText = safeExtractText(toneData);
            const sentences = toneText.split('. ');
            if (sentences.length >= 2) {
              // First question: Tone and style (first part)
              const midPoint = Math.ceil(sentences.length / 2);
              const tonePart = sentences.slice(0, midPoint).join('. ') + (sentences.slice(0, midPoint).join('. ').endsWith('.') ? '' : '.');
              setToneOfVoice(tonePart.trim());
              
              // Second question: Signature words and phrases (second part)
              const signaturePart = sentences.slice(midPoint).join('. ') + (sentences.slice(midPoint).join('. ').endsWith('.') ? '' : '.');
              setToneOfVoice2(signaturePart.trim());
            } else {
              // If only one sentence, use it for tone and leave signature empty
              setToneOfVoice(toneText);
              setToneOfVoice2('');
            }
          }
        }
        
        // Step 7: USP (Unique Selling Propositions)
        if (briefData["USP(Unique SellingPropositions)"]) {
          const uspData = briefData["USP(Unique SellingPropositions)"];
          
          // Check if it's a nested object with question fields
          if (typeof uspData === 'object' && uspData !== null) {
            if (uspData.uniqueSellingPoints) {
              setUniqueSellingPoints(safeExtractText(uspData.uniqueSellingPoints));
            }
            if (uspData.functionalBenefits) {
              setFunctionalBenefits(safeExtractText(uspData.functionalBenefits));
            }
          } else {
            // Fallback: Use string content for both fields
            const uspText = safeExtractText(uspData);
            console.log('🔧 DEBUG: USP data loaded:', uspText);
            setUniqueSellingPoints(uspText);
            // Also populate the "Think functional benefits" field
            setFunctionalBenefits(uspText);
            console.log('🔧 DEBUG: Set functionalBenefits to:', uspText);
          }
        }
        
        // Step 8: 5-Second Wow Factor
        if (briefData["5-Second Wow Factor"]) {
          setPainPoints(safeExtractText(briefData["5-Second Wow Factor"]));
        }
        
        // Step 9: Key Features (With Context)
        if (briefData["Key Features (With Context)"]) {
          setKeyFeatures(safeExtractText(briefData["Key Features (With Context)"]));
        }
        
        // Step 10: Top 6 Selling Points (With Strategic Justification)
        if (briefData["Top 6 Selling Points (With Strategic Justification)"]) {
          const pts = briefData["Top 6 Selling Points (With Strategic Justification)"];
          console.log('🔧 DEBUG: Top 6 Selling Points data type:', typeof pts);
          console.log('🔧 DEBUG: Top 6 Selling Points data:', pts);
          
          // Handle nested object structure (new format)
          if (typeof pts === 'object' && pts !== null && pts.top6SellingPoints) {
            setTop6SellingPoints(safeExtractText(pts.top6SellingPoints));
          } else if (Array.isArray(pts)) {
            const formatted = pts
              .map((p: any) => {
                if (!p) return '';
                const point = safeExtractText(p.point || p.Point || p);
                const why = safeExtractText(p.why || p.Why || '');
                return point ? `${point}${why ? ' — ' + why : ''}` : '';
              })
              .filter(Boolean)
              .join('\n');
            setTop6SellingPoints(formatted);
          } else {
            // If it's a string, store it as-is and let the formatting function handle it
            setTop6SellingPoints(String(pts));
          }
        }
        
        // Step 11: Competitive Landscape
        if (briefData["Competitive Landscape"]) {
          const comp = briefData["Competitive Landscape"];
          if (typeof comp === 'object' && comp !== null) {
            // Handle new format with separate competitor fields
            if (comp.competitor1) {
              setCompetitor1(safeExtractText(comp.competitor1));
            }
            if (comp.competitor2) {
              setCompetitor2(safeExtractText(comp.competitor2));
            }
            if (comp.competitor3) {
              setCompetitor3(safeExtractText(comp.competitor3));
            }
            if (comp.competitorComparison) {
              setCompetitorComparison(safeExtractText(comp.competitorComparison));
            }
            if (comp.amazonDifferentiators) {
              setAmazonDifferentiators(safeExtractText(comp.amazonDifferentiators));
            }
            
            // Handle old format with competitors array (fallback)
            const list = Array.isArray((comp as any).competitors) ? (comp as any).competitors : [];
            const c1 = list[0]?.name || list[0];
            const c2 = list[1]?.name || list[1];
            const c3 = list[2]?.name || list[2];
            if (c1 && !comp.competitor1) setCompetitor1(safeExtractText(c1));
            if (c2 && !comp.competitor2) setCompetitor2(safeExtractText(c2));
            if (c3 && !comp.competitor3) setCompetitor3(safeExtractText(c3));

            const diffs = Array.isArray((comp as any).amazon_differentiators) ? (comp as any).amazon_differentiators : [];
            if (diffs.length && !comp.amazonDifferentiators) {
              setAmazonDifferentiators(diffs.map((d: any) => safeExtractText(d)).join(', '));
            }

            // Only build summary if we don't have the new format fields
            if (!comp.competitorComparison) {
              const summaryPieces: string[] = [];
              if (c1 || c2 || c3) {
                summaryPieces.push([c1, c2, c3].filter(Boolean).map((x) => safeExtractText(x as any)).join(', '));
              }
              if (diffs.length) {
                summaryPieces.push(`Amazon differentiators: ${diffs.map((d: any) => safeExtractText(d)).join(', ')}`);
              }
              const pos = safeExtractText((comp as any).positioning_summary || '');
              if (pos) summaryPieces.push(pos);
              const final = summaryPieces.filter(Boolean).join(' | ');
              setCompetitorComparison(final || safeExtractText(comp));
            }
          } else {
            setCompetitorComparison(safeExtractText(comp));
          }
        }
        
        // Step 12: Search Keywords
        if (briefData["Search Keywords"]) {
          const searchData = briefData["Search Keywords"];
          
          // Check if it's a nested object with question fields
          if (typeof searchData === 'object' && searchData !== null) {
            if (searchData.searchKeywords) {
              setSearchKeywords(safeExtractText(searchData.searchKeywords));
            }
          } else {
            // Fallback: Use string content
            setSearchKeywords(safeExtractText(searchData));
          }
        }
        
        // Step 13: Brand Story
        if (briefData["Brand Story"]) {
          const brandData = briefData["Brand Story"];
          
          // Check if it's a nested object with question fields
          if (typeof brandData === 'object' && brandData !== null) {
            if (brandData.brandStory) {
              setBrandStory(safeExtractText(brandData.brandStory));
            }
            if (brandData.brandStory2) {
              setBrandStory2(safeExtractText(brandData.brandStory2));
            }
          } else {
            // Fallback: Split string content intelligently for two different questions
            const brandText = safeExtractText(brandData);
            const sentences = brandText.split('. ');
            if (sentences.length >= 2) {
              // First question: Brand origin story (first part)
              const midPoint = Math.ceil(sentences.length / 2);
              const storyPart = sentences.slice(0, midPoint).join('. ') + (sentences.slice(0, midPoint).join('. ').endsWith('.') ? '' : '.');
              setBrandStory(storyPart.trim());
              
              // Second question: Core values and purpose (second part)
              const valuesPart = sentences.slice(midPoint).join('. ') + (sentences.slice(midPoint).join('. ').endsWith('.') ? '' : '.');
              setBrandStory2(valuesPart.trim());
            } else {
              // If only one sentence, use it for story and leave values empty
              setBrandStory(brandText);
              setBrandStory2('');
            }
          }
        }
        
        // Step 14: Design Direction
        if (briefData["Design Direction"]) {
          const designData = briefData["Design Direction"];
          
          // Handle nested object structure (new format)
          if (typeof designData === 'object' && designData !== null) {
            if (designData.designDirection) {
              setDesignDirection(safeExtractText(designData.designDirection));
            }
            if (designData.designDirection2) {
              setDesignDirection2(safeExtractText(designData.designDirection2));
            }
          } else {
            // Handle string format (old format)
            const designText = safeExtractText(designData);
            
            // Split the content intelligently for two different questions
            const sentences = designText.split('. ');
            if (sentences.length >= 2) {
              // First question: Aesthetic preferences (first part)
              const midPoint = Math.ceil(sentences.length / 2);
              const aestheticPart = sentences.slice(0, midPoint).join('. ') + (sentences.slice(0, midPoint).join('. ').endsWith('.') ? '' : '.');
              setDesignDirection(aestheticPart.trim());
              
              // Second question: Visual feel and themes (second part)
              const visualPart = sentences.slice(midPoint).join('. ') + (sentences.slice(midPoint).join('. ').endsWith('.') ? '' : '.');
              setDesignDirection2(visualPart.trim());
            } else {
              // If only one sentence, use it for aesthetic and leave visual empty
              setDesignDirection(designText);
              setDesignDirection2('');
            }
          }
        }
        
        // Step 15: Final Notes & Strategic Callouts
        if (briefData["Final Notes & Strategic Callouts"]) {
          setFinalNotes(safeExtractText(briefData["Final Notes & Strategic Callouts"]));
        }
        
        // Set content ready when creative brief is loaded
        setIsContentReady(true);
        console.log('✅ Creative brief found, setting isContentReady to true');
        
        // Force resize all textareas after content is loaded
        setTimeout(() => {
          aggressiveResizeAllTextareas();
        }, 100);
        
      } catch (error) {
        console.error("Error parsing creative brief data:", error);
        // Fallback to old mapping if new structure fails
        fallbackDataMapping(brief);
      }
    }
  }, [dashboardData]);

  // No manual polling needed - useDashboardData already handles this
  
  // Fallback function for old data structure
  const fallbackDataMapping = (brief: any) => {
    try {
      // Try to parse if it's a string
      let briefData = brief;
      if (typeof brief === 'string') {
        briefData = JSON.parse(brief);
      }
      
      // Old mapping as fallback
      setProjectName(safeExtractText(briefData["Product Name"] || briefData["Project Title"]));
      setProductDescription(safeExtractText(briefData["Detailed Product Description"]));
      setProblemSolved(safeExtractText(briefData["Key Benefits"]));
      setTargetAudience2(safeExtractText(briefData["Target Audience"]));
      setCurrentChallenges(safeExtractText(briefData["Competitive Analysis"]));
      const toneData = safeExtractText(briefData["Tone of Voice"]);
      setToneOfVoice(toneData);
      setToneOfVoice2(toneData);
      
      setUniqueSellingPoints(safeExtractText(briefData["Unique Selling Proposition (USP)"]));
      setKeyFeatures(safeExtractText(briefData["Key Features"]));
      setSearchKeywords(safeExtractText(briefData["Search Keywords"]));
      
      const brandData = safeExtractText(briefData["Brand Story"]);
      // Split the content intelligently for two different questions
      const sentences = brandData.split('. ');
      if (sentences.length >= 2) {
        // First question: Brand origin story (first part)
        const midPoint = Math.ceil(sentences.length / 2);
        const storyPart = sentences.slice(0, midPoint).join('. ') + (sentences.slice(0, midPoint).join('. ').endsWith('.') ? '' : '.');
        setBrandStory(storyPart.trim());
        
        // Second question: Core values and purpose (second part)
        const valuesPart = sentences.slice(midPoint).join('. ') + (sentences.slice(midPoint).join('. ').endsWith('.') ? '' : '.');
        setBrandStory2(valuesPart.trim());
      } else {
        // If only one sentence, use it for story and leave values empty
        setBrandStory(brandData);
        setBrandStory2('');
      }
      
      const designData = safeExtractText(briefData["Design Direction"]);
      // Split the content intelligently for two different questions
      const designSentences = designData.split('. ');
      if (designSentences.length >= 2) {
        // First question: Aesthetic preferences (first part)
        const midPoint = Math.ceil(designSentences.length / 2);
        const aestheticPart = designSentences.slice(0, midPoint).join('. ') + (designSentences.slice(0, midPoint).join('. ').endsWith('.') ? '' : '.');
        setDesignDirection(aestheticPart.trim());
        
        // Second question: Visual feel and themes (second part)
        const visualPart = designSentences.slice(midPoint).join('. ') + (designSentences.slice(midPoint).join('. ').endsWith('.') ? '' : '.');
        setDesignDirection2(visualPart.trim());
      } else {
        // If only one sentence, use it for aesthetic and leave visual empty
        setDesignDirection(designData);
        setDesignDirection2('');
      }
      setFinalNotes(safeExtractText(briefData["Final Notes & Strategic Callouts"]));
      
      setIsContentReady(true);
      console.log('✅ Fallback data mapping successful, setting isContentReady to true');
      
      // Force resize all textareas after fallback content is loaded
      setTimeout(() => {
        aggressiveResizeAllTextareas();
      }, 100);
    } catch (error) {
      console.error("Fallback data mapping also failed:", error);
    }
  };
  
  // Remove mock demographic initialization; will be set from brief data when available

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center" style={{ background: 'linear-gradient(248deg, #B391E8 6.01%, #FDFDFF 98.77%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#6B4EFF] mx-auto mb-4"></div>
          <p className="text-[#272264] text-lg">Loading your project data...</p>
          <p className="text-[#272264] text-sm mt-2">Preparing to generate your creative brief</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center" style={{ background: 'linear-gradient(248deg, #B391E8 6.01%, #FDFDFF 98.77%)' }}>
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">Error loading creative brief</p>
          <button 
            onClick={() => navigate('/listing-onboarding')}
            className="px-6 py-2 bg-[#6B4EFF] text-white rounded-lg"
          >
            Back to Onboarding
          </button>
        </div>
      </div>
    );
  }

  // Show loader until creative brief exists to avoid empty form while generating
  const hasProjectData = dashboardData?.project;
  const hasCreativeBrief = !!(extractBrief(dashboardData as any) || extractBriefFromHistory(historyResponse as any));
  if (!isContentReady) {
    
    console.log('Content not ready - hasProjectData:', !!hasProjectData, 'hasCreativeBrief:', !!hasCreativeBrief, 'isContentReady:', isContentReady);
    
    let message = "Generating your Onboarding brief...";
    let subMessage = "AI is analyzing your data and creating comprehensive content";
    
    if (hasProjectData) {
      message = "AI is generating your Onboarding brief...";
      subMessage = "This may take 30-60 seconds while we analyze your data and create your content";
    } else {
      message = "Loading your project data...";
      subMessage = "Preparing to generate your Onboarding brief";
    }
    
    return (
      <div className="min-h-screen w-full flex items-center justify-center" style={{ background: 'linear-gradient(248deg, #B391E8 6.01%, #FDFDFF 98.77%)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#6B4EFF] mx-auto mb-4"></div>
          <p className="text-[#272264] text-lg">{message}</p>
          <p className="text-[#272264] text-sm mt-2">{subMessage}</p>
          <p className="text-[#272264] text-xs mt-4 opacity-70">Please wait - the form will appear once content generation is complete</p>
        </div>
      </div>
    );
  }


  // Function to save current step data
  const saveCurrentStep = async () => {
    if (isSaving || !projectId) return; // Prevent multiple saves and ensure projectId exists
    
    setIsSaving(true);
    setSaveStatus('saving');
    console.log('Saving current step data for step:', activeStep);
    
    try {
      // Collect all current form data and map to backend section names
      const briefData = {
        // Project Overview - these are stored in the project table, not brief_json
        // But we'll include them for completeness
        "Project Name": projectName,
        "Brand Name": brandName,
        "Website Link": websiteLink,
        "Amazon Listing": amazonListing,
        "Instagram": instagram,
        
        // Product Snapshot - use the field names that project summary expects
        "Product Snapshot": productDescription,
        "What problem does it solve?": problemSolved,
        "Who is it meant for?": targetAudience2,
        
        // Current Challenges
        "Current Listing Challenges": currentChallenges,
        "Conversion Loss": conversionLoss,
        
        // Target Customer Deep Dive - create the nested object structure
        "Target Customer Deep Dive": {
          "Location": location,
          "Income": income,
          "Profession": profession,
          "Life Stage": lifeStage,
          "Demographics": demographics,
          "Psychographics": psychographics,
          "Pain Points": painPoints,
          "How do they shop on Amazon?": shoppingBehavior,
          // Store explicit Gender and Age strings derived from chart state when available
          "Gender": (() => {
            const male = genderData.find(g => g.name === 'Male')?.value || 0;
            const female = genderData.find(g => g.name === 'Female')?.value || 0;
            const other = genderData.find(g => g.name === 'Other')?.value || 0;
            const parts = [] as string[];
            if (male > 0) parts.push(`${Math.round(male)}% male`);
            if (female > 0) parts.push(`${Math.round(female)}% female`);
            if (other > 0) parts.push(`${Math.round(other)}% other`);
            return parts.length ? parts.join(' / ') : demographics;
          })(),
          "Age": (() => {
            // Prefer ageData slices (either core/secondary bands or buckets)
            if (ageData && ageData.length > 0) {
              return ageData
                .filter(a => (a.value || 0) > 0)
                .map(a => `${a.name}: ${Math.round(a.value)}%`)
                .join(', ');
            }
            return demographics;
          })(),
          "Identity Tags": []
        },
        
        // Tone of Voice
        "Tone of Voice": toneOfVoice,
        "Tone of Voice 2": toneOfVoice2,
        
        // USP
        "USP(Unique SellingPropositions)": uniqueSellingPoints,
        "Think functional benefits, emotional angles, and cultural relevance": functionalBenefits,
        
        // Key Features
        "Key Features (With Context)": keyFeatures,
        "Top 6 Selling Points (With Strategic Justification)": top6SellingPoints,
        
        // Competitive Landscape
        "Competitive Landscape": {
          "Competitor 1": competitor1,
          "Competitor 2": competitor2,
          "Competitor 3": competitor3,
          "Describe how this product compares": competitorComparison,
          "Amazon Differentiators": amazonDifferentiators
        },
        
        // Search Keywords
        "Search Keywords": searchKeywords,
        
        // Brand Story
        "Brand Story": brandStory,
        "Brand Story 2": brandStory2,
        
        // Design Direction
        "Design Direction": designDirection,
        "Design Direction 2": designDirection2,
        
        // Final Notes
        "Final Notes & Strategic Callouts": finalNotes
      };
      
      console.log('Current step data to save:', briefData);
      
      // Save to backend
      console.log('🔧 DEBUG: Attempting to save data to backend...');
      console.log('🔧 DEBUG: Project ID:', projectId);
      console.log('🔧 DEBUG: Brief data keys:', Object.keys(briefData));
      
      const result = await saveCreativeBrief.mutateAsync({
        project_id: projectId,
        brief_data: briefData
      });
      
      console.log('🔧 DEBUG: Save result:', result);
      console.log('Step data saved successfully!');
      setSaveStatus('saved');
      
      // Reset to idle after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
      
    } catch (error) {
      console.error('🔧 DEBUG: Error saving step data:', error);
      console.error('🔧 DEBUG: Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        error: error
      });
      setSaveStatus('error');
      
      // Reset to idle after 3 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const approveCurrentStep = async () => {
    const currentStep = activeStep; // Store the current step before it changes
    console.log('Approve clicked for step:', currentStep);
    
    setApprovedSteps(prev => {
      const newSet = new Set([...prev, activeStep]);
      return newSet;
    });
    const nextStep = activeStep + 1;
    if (nextStep < stepContents.length) {
      setActiveStep(nextStep);
    }
    
    // Only scroll stepper after USP step (index 6)
    if (currentStep === 6) {
      console.log('USP step approved - scrolling stepper');
      setTimeout(() => {
        const stepperContainer = document.querySelector('.stepper-container');
        if (stepperContainer) {
          // Calculate position to show the next step at the top
          const nextStepPosition = nextStep * 88; // Use fixed step height for consistency
          console.log('Scrolling to position:', nextStepPosition);
          // Ensure we don't scroll beyond the container's scrollable area
          const maxScroll = stepperContainer.scrollHeight - stepperContainer.clientHeight;
          stepperContainer.scrollTop = Math.min(nextStepPosition, maxScroll);
        }
      }, 100);
    } else {
      console.log('Not step 7, no scrolling. Current step:', currentStep);
    }
  };

  const stepContents = [
    // Step 1: Project Overview
    <div data-step="project-overview">
      <div className="flex items-center justify-between mb-6">
        <div></div>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 1/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Project Overview</div>
      <div>
        <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Project Name</label>
        <input 
          type="text"
          className="w-full px-4 py-2 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]" 
          style={{ 
            color: 'rgba(0, 0, 0, 0.70)', 
            fontSize: '16px', 
            fontStyle: 'normal', 
            fontWeight: '400'
          }} 
          value={projectName} 
          onChange={(e) => setProjectName(e.target.value)} 
        />
      </div>
      <div className="mt-8">
        <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Brand Name</label>
        <input 
          type="text"
          className="w-full px-4 py-2 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]" 
          style={{ 
            color: 'rgba(0, 0, 0, 0.70)', 
            fontSize: '16px', 
            fontStyle: 'normal', 
            fontWeight: '400'
          }} 
          value={brandName} 
          onChange={(e) => setBrandName(e.target.value)} 
        />
      </div>
      <div className="mt-8">
        <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Website Link</label>
        <input 
          type="text"
          className="w-full px-4 py-2 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]" 
          style={{ 
            color: 'rgba(0, 0, 0, 0.70)', 
            fontSize: '16px', 
            fontStyle: 'normal', 
            fontWeight: '400'
          }} 
          value={websiteLink} 
          onChange={(e) => setWebsiteLink(e.target.value)} 
        />
      </div>
      <div className="mt-8">
        <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Amazon Listing (If Available)</label>
        <textarea 
          className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" 
          style={{ 
            color: 'rgba(0, 0, 0, 0.70)', 
            fontSize: '16px', 
            fontStyle: 'normal', 
            fontWeight: '400', 
            lineHeight: 'normal',
            height: '48px',
            minHeight: '48px',
            maxHeight: '48px'
          }} 
          value={amazonListing} 
          onChange={(e) => setAmazonListing(e.target.value)} 
        />
      </div>
      <div className="mt-8">
        <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Instagram (if applicable)</label>
        <textarea 
          className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" 
          style={{ 
            color: 'rgba(0, 0, 0, 0.70)', 
            fontSize: '16px', 
            fontStyle: 'normal', 
            fontWeight: '400', 
            lineHeight: 'normal',
            height: '48px',
            minHeight: '48px',
            maxHeight: '48px'
          }} 
          value={instagram} 
          onChange={(e) => setInstagram(e.target.value)} 
        />
      </div>
      
      {/* Assets Section */}
      {dashboardData?.project?.has_assets && (
        <div className="mt-8">
          <label className="block text-[#272264] font-semibold mb-4 text-sm tracking-wide">Assets & Media Links</label>
          <div className="space-y-4">
            {dashboardData.project.white_background_link && (
              <div>
                <label className="block text-[#272264] font-semibold mb-1 text-xs tracking-wide">White Background Images</label>
                <div className="px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] text-[#272264] text-sm">
                  <a 
                    href={dashboardData.project.white_background_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#564DB8] hover:underline break-all"
                  >
                    {dashboardData.project.white_background_link}
                  </a>
                </div>
              </div>
            )}
            
            {dashboardData.project.old_images_link && (
              <div>
                <label className="block text-[#272264] font-semibold mb-1 text-xs tracking-wide">Old/Current Images</label>
                <div className="px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] text-[#272264] text-sm">
                  <a 
                    href={dashboardData.project.old_images_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#564DB8] hover:underline break-all"
                  >
                    {dashboardData.project.old_images_link}
                  </a>
                </div>
              </div>
            )}
            
            {dashboardData.project.lifestyle_image_link && (
              <div>
                <label className="block text-[#272264] font-semibold mb-1 text-xs tracking-wide">Lifestyle Images</label>
                <div className="px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] text-[#272264] text-sm">
                  <a 
                    href={dashboardData.project.lifestyle_image_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#564DB8] hover:underline break-all"
                  >
                    {dashboardData.project.lifestyle_image_link}
                  </a>
                </div>
              </div>
            )}
            
            {dashboardData.project.user_generated_content_link && (
              <div>
                <label className="block text-[#272264] font-semibold mb-1 text-xs tracking-wide">User Generated Content</label>
                <div className="px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] text-[#272264] text-sm">
                  <a 
                    href={dashboardData.project.user_generated_content_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#564DB8] hover:underline break-all"
                  >
                    {dashboardData.project.user_generated_content_link}
                  </a>
                </div>
              </div>
            )}
            
            {dashboardData.project.video_content_link && (
              <div>
                <label className="block text-[#272264] font-semibold mb-1 text-xs tracking-wide">Video Content</label>
                <div className="px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] text-[#272264] text-sm">
                  <a 
                    href={dashboardData.project.video_content_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#564DB8] hover:underline break-all"
                  >
                    {dashboardData.project.video_content_link}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* No Assets Reason */}
      {dashboardData?.project && !dashboardData.project.has_assets && dashboardData.project.no_assets_reason && (
        <div className="mt-8">
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Reason for No Assets</label>
          <div className="px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] text-[#272264] text-sm">
            {dashboardData.project.no_assets_reason}
          </div>
        </div>
      )}
      
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 2: Product Snapshot (restored, ensure no duplicate back arrow or step badge)
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 2/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Product Snapshot</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">What exactly is the product?</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: 'normal',
                paddingBottom: '60px'
              }}
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button onClick={() => openRegeneratePopup('Product Snapshot', 'productName')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto relative z-10" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => openHistoryPopup('Product Snapshot', 'productName')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto relative z-10" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">What does it do and how does it work?</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: 'normal',
                paddingBottom: '60px'
              }}
              value={formatForDisplay(productDescription)}
              onChange={(e) => setProductDescription(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10">
              <button onClick={() => openRegeneratePopup('Product Snapshot', 'productDescription')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm relative z-10" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => openHistoryPopup('Product Snapshot', 'productDescription')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm relative z-10" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">What problem does it solve?</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} value={formatForDisplay(problemSolved)} onChange={(e) => setProblemSolved(e.target.value)} onInput={handleTextareaResize} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button onClick={() => openRegeneratePopup('Product Snapshot', 'problemSolved')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => openHistoryPopup('Product Snapshot', 'problemSolved')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Who is it meant for?</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} value={formatForDisplay(targetAudience2)} onChange={(e) => {
              console.log('🔧 DEBUG: Textarea onChange triggered with value:', e.target.value);
              setTargetAudience2(e.target.value);
            }} onInput={handleTextareaResize} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button onClick={() => openRegeneratePopup('Product Snapshot', 'targetAudience')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => openHistoryPopup('Product Snapshot', 'targetAudience')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 3: Current Listing Challenges
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 3/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Current Listing Challenges</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">What's broken or underwhelming about the current Amazon listing, brand positioning, or creative execution?</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} value={formatForDisplay(currentChallenges)} onChange={(e) => setCurrentChallenges(e.target.value)} onInput={handleTextareaResize} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button onClick={() => openRegeneratePopup('Current Listing Challenges', 'currentChallenges')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => openHistoryPopup('Current Listing Challenges', 'currentChallenges')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Where are they losing conversions or attention?</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} value={formatForDisplay(conversionLoss)} onChange={(e) => setConversionLoss(e.target.value)} onInput={handleTextareaResize} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button onClick={() => openRegeneratePopup('Current Listing Challenges', 'conversionLoss')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => { setCurrentSection('Current Listing Challenges'); setShowHistoryPopup(true); }} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 4: Target Customer Deep Dive
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 4/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Target Customer Deep Dive</div>
      <div className="flex flex-col gap-8">
        {/* Gender Section with Pie Chart */}
        <div>
          <label className="block text-[#272264] font-semibold mb-4 text-sm tracking-wide">Gender</label>
          <div className="flex items-center justify-center mb-6">
            <div className="relative" style={{ width: '400px', height: '400px' }}>
              {/* Dynamic Pie Chart */}
              <svg key={`gender-chart-${pieChartKey}`} width="400" height="400" viewBox="0 0 300 300">
                {(() => {
                  let currentAngle = 0;
                  
                  return genderData.map((item, index) => {
                    const percentage = item.value; // Use pre-calculated percentage
                    const angle = (percentage / 100) * 360;
                    const startAngle = currentAngle;
                    currentAngle += angle;
                    
                    const startRad = (startAngle - 90) * (Math.PI / 180);
                    const endRad = (currentAngle - 90) * (Math.PI / 180);
                    
                    const x1 = 150 + 80 * Math.cos(startRad);
                    const y1 = 150 + 80 * Math.sin(startRad);
                    const x2 = 150 + 80 * Math.cos(endRad);
                    const y2 = 150 + 80 * Math.sin(endRad);
                    
                    const largeArcFlag = angle > 180 ? 1 : 0;
                    
                    const pathData = [
                      `M 150 150`,
                      `L ${x1} ${y1}`,
                      `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                      'Z'
                    ].join(' ');
                    
                    // Calculate label position
                    const labelAngle = startAngle + angle / 2;
                    const labelRad = (labelAngle - 90) * (Math.PI / 180);
                    const labelX = 150 + 120 * Math.cos(labelRad);
                    const labelY = 150 + 120 * Math.sin(labelRad);
                    
                    return (
                      <g key={index}>
                        <path d={pathData} fill={item.color} />
                        <text 
                          x={labelX} 
                          y={labelY} 
                          textAnchor="middle" 
                          fill="black" 
                          fontSize="16" 
                          fontWeight="bold"
                        >
                          {item.percentage}
                        </text>
                      </g>
                    );
                  });
                })()}
              </svg>
            </div>
          </div>
          {/* Dynamic Legend */}
          <div className="flex justify-center gap-8">
            {genderData.map((item, index) => {
              return (
                <div key={index} className="flex items-center gap-3">
                  <div 
                    className="w-5 h-5 shadow-sm" 
                    style={{ backgroundColor: item.color }}
                  ></div>
                  <span className="text-sm font-medium text-[#272264]">
                    {item.name} ({item.percentage})
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Age Section with Pie Chart */}
        <div>
          <label className="block text-[#272264] font-semibold mb-4 text-sm tracking-wide">Age</label>
          <div className="flex items-center justify-center mb-6">
            <div className="relative" style={{ width: '400px', height: '400px' }}>
              {/* Dynamic Age Pie Chart */}
              <svg key={`age-chart-${pieChartKey}`} width="400" height="400" viewBox="0 0 300 300">
                {(() => {
                  let currentAngle = 0;
                  
                  return ageData.map((item, index) => {
                    const percentage = item.value || (ageData.length === 1 ? 100 : (index === 0 ? 100 : 0));
                    const angle = (percentage / 100) * 360;
                    const startAngle = currentAngle;
                    currentAngle += angle;
                    
                    const startRad = (startAngle - 90) * (Math.PI / 180);
                    const endRad = (currentAngle - 90) * (Math.PI / 180);
                    
                    const x1 = 150 + 80 * Math.cos(startRad);
                    const y1 = 150 + 80 * Math.sin(startRad);
                    const x2 = 150 + 80 * Math.cos(endRad);
                    const y2 = 150 + 80 * Math.sin(endRad);
                    
                    const largeArcFlag = angle > 180 ? 1 : 0;
                    
                    const pathData = [
                      `M 150 150`,
                      `L ${x1} ${y1}`,
                      `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                      'Z'
                    ].join(' ');
                    
                    // Calculate label position
                    const labelAngle = startAngle + angle / 2;
                    const labelRad = (labelAngle - 90) * (Math.PI / 180);
                    const labelX = 150 + 120 * Math.cos(labelRad);
                    const labelY = 150 + 120 * Math.sin(labelRad);
                    
                    return (
                      <g key={index}>
                        <path d={pathData} fill={item.color} />
                        <text x={labelX} y={labelY} textAnchor="middle" fill="black" fontSize="14" fontWeight="bold">
                          {item.name}
                        </text>
                      </g>
                    );
                  });
                })()}
              </svg>
            </div>
          </div>
          {/* Dynamic Age Legend */}
          <div className="flex justify-center gap-8">
            {ageData
              .filter(i => i.value > 0 || ageData.length === 1)
              .map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-5 h-5 shadow-sm" style={{ backgroundColor: item.color }}></div>
                  <span className="text-sm font-medium text-[#272264]">{item.name}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Location Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Location</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', height: '120px', minHeight: '120px', maxHeight: '120px', paddingBottom: '60px' }} value={location} onChange={(e) => setLocation(e.target.value)} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'location')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>Regenerate</button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'location')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>History</button>
            </div>
          </div>
        </div>

        {/* Income Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Income</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', height: '120px', minHeight: '120px', maxHeight: '120px', paddingBottom: '60px' }} value={income} onChange={(e) => setIncome(e.target.value)} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'income')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'income')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>

        {/* Profession Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Profession</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', height: '120px', minHeight: '120px', maxHeight: '120px', paddingBottom: '60px' }} value={profession} onChange={(e) => setProfession(e.target.value)} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'profession')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'profession')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>

        {/* Life Stage Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Life Stage or Identity (e.g., new moms, eco-conscious Gen Z, busy professionals)</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} value={lifeStage} onChange={(e) => setLifeStage(e.target.value)} onInput={handleTextareaResize} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'lifeStage')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'lifeStage')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>

        {/* Pain Points Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Pain Points, Desires, Motivations</label>
          <div className="relative">
            <textarea className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} value={formatForDisplay(painPoints)} onChange={(e) => setPainPoints(e.target.value)} onInput={handleTextareaResize} />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'painPoints')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'painPoints')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>

        {/* Amazon Shopping Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">How do they shop on Amazon? What do they care about when scrolling?</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" 
              style={{ resize: 'none', color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal' }} 
              value={shoppingBehavior}
              onChange={(e) => setShoppingBehavior(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'shoppingBehavior')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'shoppingBehavior')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>

        {/* Demographics Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Demographics (Age, Gender, Income, Location, Education, etc.)</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" 
              style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} 
              value={demographics} 
              onChange={(e) => {
                setDemographics(e.target.value);
                // Sync pie charts when demographics change
                syncProductSnapshotToTargetCustomer(e.target.value);
              }} 
              onInput={handleTextareaResize} 
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'demographics')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'demographics')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>

        {/* Psychographics Section */}
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Psychographics (Values, Attitudes, Lifestyles, Interests, Personality)</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" 
              style={{ color: 'rgba(0, 0, 0, 0.70)', fontSize: '16px', fontStyle: 'normal', fontWeight: '400', lineHeight: 'normal', paddingBottom: '60px' }} 
              value={psychographics} 
              onChange={(e) => setPsychographics(e.target.value)} 
              onInput={handleTextareaResize} 
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Target Customer Deep Dive', 'psychographics')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Target Customer Deep Dive', 'psychographics')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 5: Barriers To Purchase
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 5/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Barriers To Purchase</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">List the common doubts, hesitations, or FAQ-style friction points that stop people from buying — even if they like the product.</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6',
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(barriersToPurchase)}
              onChange={(e) => setBarriersToPurchase(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Barriers To Purchase', 'barriersToPurchase')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => { setCurrentSection('Barriers To Purchase'); setShowHistoryPopup(true); }} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 6: Brand Voice & Tone
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 6/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Brand Voice & Tone</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Describe the tone and copywriting style the brand uses or should use (e.g., bold, sassy, informative, premium, conversational).</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: 'normal',
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(toneOfVoice)}
              onChange={(e) => setToneOfVoice(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Brand Voice & Tone', 'toneOfVoice')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Brand Voice & Tone', 'toneOfVoice')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold mb-1 text-sm tracking-wide">Include any signature words, phrases, or linguistic quirks.</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: 'normal',
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={toneOfVoice2}
              onChange={(e) => setToneOfVoice2(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Brand Voice & Tone', 'toneOfVoice2')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Brand Voice & Tone', 'toneOfVoice2')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 7: USP (Unique Selling Propositions)
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 7/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">USP (Unique Selling Propositions)</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>What makes this product meaningfully different from other options in the category?</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: 'normal', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(uniqueSellingPoints)}
              onChange={(e) => setUniqueSellingPoints(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('USP(Unique SellingPropositions)', 'uniqueSellingPoints')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('USP(Unique SellingPropositions)', 'uniqueSellingPoints')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Think functional benefits, emotional angles, and cultural relevance.</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: 'normal', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(functionalBenefits)}
              onChange={(e) => setFunctionalBenefits(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2" style={{ margin: '10px 0' }}>
              <button type="button" onClick={() => openRegeneratePopup('USP(Unique SellingPropositions)', 'functionalBenefits')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('USP(Unique SellingPropositions)', 'functionalBenefits')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 8: 5-Second Wow Factor
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 8/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">5-Second Wow Factor</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>If a customer saw this listing for 5 seconds, what single visual hook, copy line, or feature would stop them in their tracks?</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: 'normal', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(painPoints)}
              onChange={(e) => setPainPoints(e.target.value)}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('5-Second Wow Factor', 'painPoints')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => { setCurrentSection('5-Second Wow Factor'); setShowHistoryPopup(true); }} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 9: Key Features (With Context)
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 9/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Key Features (With Context)</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>List 4–6 major features. But go beyond just the bullet points — explain: Why does this matter to the buyer? How does it connect to their lifestyle or values?</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(keyFeatures)}
              onChange={(e) => setKeyFeatures(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button onClick={() => openRegeneratePopup('Key Features (With Context)', 'keyFeatures')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => { setCurrentSection('Key Features (With Context)'); setShowHistoryPopup(true); }} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 10: Top 6 Selling Points (With Strategic Justification)
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 10/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Top 6 Selling Points (With Strategic Justification)</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>For each of the client's selected selling points: State the point. Explain *why* it's strategically powerful for this product and customer.</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatSellingPointsForDisplay(top6SellingPoints)}
              onChange={(e) => setTop6SellingPoints(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button onClick={() => openRegeneratePopup('Top 6 Selling Points (With Strategic Justification)', 'functionalBenefits')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => { setCurrentSection('Top 6 Selling Points (With Strategic Justification)'); setShowHistoryPopup(true); }} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 11: Competitive Landscape
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 11/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Competitive Landscape</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>List 2–3 main competitors</label>
          <div className="space-y-4">
            <div className="relative">
              <textarea 
                className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
                style={{ 
                  color: 'rgba(0, 0, 0, 0.70)', 
                  fontSize: '16px', 
                  fontStyle: 'normal', 
                  fontWeight: '400', 
                  lineHeight: '1.6'
                }}
                value={competitor1}
                onChange={(e) => setCompetitor1(e.target.value)} onInput={handleTextareaResize}
              />
            </div>
            <div className="relative">
              <textarea 
                className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
                style={{ 
                  color: 'rgba(0, 0, 0, 0.70)', 
                  fontSize: '16px', 
                  fontStyle: 'normal', 
                  fontWeight: '400', 
                  lineHeight: '1.6'
                }}
                value={competitor2}
                onChange={(e) => setCompetitor2(e.target.value)} onInput={handleTextareaResize}
              />
            </div>
            <div className="relative">
              <textarea 
                className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
                style={{ 
                  color: 'rgba(0, 0, 0, 0.70)', 
                  fontSize: '16px', 
                  fontStyle: 'normal', 
                  fontWeight: '400', 
                  lineHeight: '1.6'
                }}
                value={competitor3}
                onChange={(e) => setCompetitor3(e.target.value)} onInput={handleTextareaResize}
              />
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Describe how this product compares</label>
          <div className="relative">
                          <textarea 
                className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
                style={{ 
                  color: 'rgba(0, 0, 0, 0.70)', 
                  fontSize: '16px', 
                  fontStyle: 'normal', 
                  fontWeight: '400', 
                  lineHeight: '1.6', 
                  paddingBottom: '60px',
                  overflowY: 'auto'
                }}
                value={formatForDisplay(competitorComparison)}
                onChange={(e) => setCompetitorComparison(e.target.value)} onInput={handleTextareaResize}
              />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Competitive Landscape', 'competitorComparison')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Competitive Landscape', 'competitorComparison')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Mention any Amazon-specific differentiators (e.g. bundle, shipping time, design)</label>
          <div className="relative">
                          <textarea 
                className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
                style={{ 
                  color: 'rgba(0, 0, 0, 0.70)', 
                  fontSize: '16px', 
                  fontStyle: 'normal', 
                  fontWeight: '400', 
                  lineHeight: '1.6', 
                  paddingBottom: '60px',
                  overflowY: 'auto'
                }}
                value={amazonDifferentiators}
                onChange={(e) => setAmazonDifferentiators(e.target.value)} onInput={handleTextareaResize}
              />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Competitive Landscape', 'amazonDifferentiators')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Competitive Landscape', 'amazonDifferentiators')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 12: Search & Keywords Strategy
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 12/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Search & Keywords Strategy</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Suggest relevant search terms and niche keywords to target. These should align with user intent, category trends, or long-tail SEO goals.</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(searchKeywords)}
              onChange={(e) => setSearchKeywords(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Search & Keywords Strategy', 'searchKeywords')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => { setCurrentSection('Search & Keywords Strategy'); setShowHistoryPopup(true); }} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 13: Brand Story, Values & Purpose
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 13/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Brand Story, Values & Purpose</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Give a short but meaningful brand origin story or founder story.</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(brandStory)}
              onChange={(e) => setBrandStory(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Brand Story, Values & Purpose', 'brandStory')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Brand Story, Values & Purpose', 'brandStory')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Highlight core values, emotional drivers, or the "bigger why" behind the brand's existence.</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={brandStory2}
              onChange={(e) => setBrandStory2(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Brand Story, Values & Purpose', 'brandStory2')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Brand Story, Values & Purpose', 'brandStory2')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 14: Design Direction
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 14/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Design Direction</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Summarize the client's aesthetic preferences</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF] resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(designDirection)}
              onChange={(e) => setDesignDirection(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Design Direction', 'designDirection')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Design Direction', 'designDirection')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Suggest how the visuals, layout, or color themes should feel (e.g., clean/minimal, bold/graphic, warm/natural)</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={designDirection2}
              onChange={(e) => setDesignDirection2(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Design Direction', 'designDirection2')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button type="button" onClick={() => openHistoryPopup('Design Direction', 'designDirection2')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex gap-4 mt-6 justify-end w-full">
        <button 
          className="px-7 py-2 font-semibold text-base bg-white text-black transition" 
          style={{ 
            borderRadius: '12px', 
            border: '1px solid #000', 
            opacity: isSaving ? 0.6 : 1,
            backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'error' ? '#f8d7da' : 'white'
          }} 
          onClick={saveCurrentStep}
          disabled={isSaving}
        >
          {saveStatus === 'saving' ? 'Saving...' : 
           saveStatus === 'saved' ? 'Saved!' : 
           saveStatus === 'error' ? 'Error' : 
           'Save Section'}
        </button>
        <button 
          className="px-7 py-2 font-semibold text-base text-white shadow-md transition" 
          style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)', opacity: isSaving ? 0.6 : 1 }} 
          onClick={approveCurrentStep}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Approve Section'}
        </button>
      </div>
    </div>,
    // Step 15: Final Notes & Strategic Callouts
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
          className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-[#F4F3FF] transition"
          style={{ border: '1px solid #E2D5FF' }}
          aria-label="Back"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="text-xs bg-[#E6E3FF] text-[#6B4EFF] px-4 py-1 rounded-lg font-semibold tracking-wide shadow-sm">Step 15/15</div>
      </div>
      <div className="text-[#272264] font-extrabold text-2xl mb-6">Final Notes & Strategic Callouts</div>
      <div className="flex flex-col gap-8">
        <div>
          <label className="block text-[#272264] font-semibold tracking-wide" style={{ fontSize: '16px', marginBottom: '16px' }}>Include any extra insights for the creative team, such as: Packaging or compliance considerations, Customer education needs, Cross-sell or upsell potential, Social proof or influencer angle</label>
          <div className="relative">
            <textarea 
              className="w-full px-4 py-3 rounded-lg border border-[#E2D5FF] bg-[#F4F3FF] focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]  resize-none overflow-hidden" 
              style={{ 
                color: 'rgba(0, 0, 0, 0.70)', 
                fontSize: '16px', 
                fontStyle: 'normal', 
                fontWeight: '400', 
                lineHeight: '1.6', 
                paddingBottom: '60px',
                overflowY: 'auto'
              }}
              value={formatForDisplay(finalNotes)}
              onChange={(e) => setFinalNotes(e.target.value)} onInput={handleTextareaResize}
            />
            <div className="absolute left-2 bottom-2 flex gap-2 mb-2 z-10 pointer-events-none">
              <button type="button" onClick={() => openRegeneratePopup('Final Notes & Strategic Callouts', 'finalNotes')} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M0 9.65409C5.187 9.65409 6.55186 16.206 6.55186 16.206C6.55186 16.206 7.91671 9.65409 13.1037 9.65409C7.91671 9.65409 6.55186 3.10217 6.55186 3.10217C6.55186 3.10217 5.187 9.65409 0 9.65409Z" fill="#4F3E98"/>
                  <path d="M10.3452 16.2068C13.5556 16.9009 13.7936 20 13.7936 20C13.7936 20 14.0315 16.9009 17.2419 16.2068C14.0315 15.5126 13.7936 12.4136 13.7936 12.4136C13.7936 12.4136 13.5556 15.5126 10.3452 16.2068Z" fill="#4F3E98"/>
                  <path d="M11.0347 4.82773C12.1978 5.05193 13.2593 5.64072 14.0653 6.50875C14.8713 7.37677 15.38 8.47892 15.5175 9.65545C15.6551 8.47892 16.1637 7.37677 16.9697 6.50875C17.7757 5.64072 18.8372 5.05193 20.0004 4.82773C18.8372 4.60352 17.7757 4.01473 16.9697 3.14671C16.1637 2.27868 15.6551 1.17653 15.5175 0C15.38 1.17653 14.8713 2.27868 14.0653 3.14671C13.2593 4.01473 12.1978 4.60352 11.0347 4.82773Z" fill="#4F3E98"/>
                </svg>
                Regenerate
              </button>
              <button onClick={() => { setCurrentSection('Final Notes & Strategic Callouts'); setShowHistoryPopup(true); }} className="text-[#6B4EFF] hover:text-[#5A4ABE] text-sm font-medium px-3 py-1 flex items-center gap-2 shadow-sm pointer-events-auto" style={{ borderRadius: '12px', border: '1px solid #E2D5FF', background: '#FFF' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 18C6.7 18 4.696 17.2377 2.988 15.713C1.28 14.1883 0.300667 12.284 0.05 10H2.1C2.33333 11.7333 3.10433 13.1667 4.413 14.3C5.72167 15.4333 7.25067 16 9 16C10.95 16 12.6043 15.321 13.963 13.963C15.3217 12.605 16.0007 10.9507 16 9C15.9993 7.04933 15.3203 5.39533 13.963 4.038C12.6057 2.68067 10.9513 2.00133 9 2C7.85 2 6.775 2.26667 5.775 2.8C4.775 3.33333 3.93333 4.06667 3.25 5H6V7H0V1H2V3.35C2.85 2.28333 3.88767 1.45833 5.113 0.875C6.33833 0.291667 7.634 0 9 0C10.25 0 11.421 0.237667 12.513 0.713C13.605 1.18833 14.555 1.82967 15.363 2.637C16.171 3.44433 16.8127 4.39433 17.288 5.487C17.7633 6.57967 18.0007 7.75067 18 9C17.9993 10.2493 17.762 11.4203 17.288 12.513C16.814 13.6057 16.1723 14.5557 15.363 15.363C14.5537 16.1703 13.6037 16.812 12.513 17.288C11.4223 17.764 10.2513 18.0013 9 18ZM11.8 13.2L8 9.4V4H10V8.6L13.2 11.8L11.8 13.2Z" fill="#4F3E98"/>
                </svg>
                History
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-6 w-full">
        <button className="w-full px-7 py-3 font-semibold text-base text-white shadow-md transition" style={{ borderRadius: '12px', background: 'linear-gradient(90deg, #362E7F 0%, #5A4ABE 45.67%, #7F60BF 100%)' }} onClick={() => {
          // Show submission successful screen with project ID
          if (projectId) {
            window.location.href = `/submission-successful/${projectId}`;
          } else {
            window.location.href = '/submission-successful';
          }
        }}>Complete Onboarding</button>
      </div>
    </div>,
  ];

  // Regenerate Popup
  const RegeneratePopup = () => {
    const queryClient = useQueryClient();
    const [feedback, setFeedback] = useState('');
    // Merge custom prompt features into single feedback field
    const [taskId, setTaskId] = useState<string | null>(null);

    // Use the real regenerate hook
    const regenerateSection = useRegenerateSection();
    
    // Poll task status when we have a task ID
    // Always poll during regeneration, but disable polling for dashboard data when user is editing
    const { data: taskStatus } = useTaskStatus(taskId, {
      enabled: !!taskId,
      refetchInterval: 1000, // Poll every 1 second during regeneration
    });

    // Reset task ID when popup is closed
    useEffect(() => {
      if (!showRegeneratePopup) {
        setTaskId(null);
      }
    }, [showRegeneratePopup]);

    // Handle successful regeneration
    useEffect(() => {
      console.log('🔧 DEBUG: Task status effect triggered:', (taskStatus as any)?.status);
      if ((taskStatus as any)?.status === 'completed') {
        console.log('🔧 DEBUG: Task completed, processing result...');
        console.log('🔧 DEBUG: Task result:', (taskStatus as any)?.result);
        console.log('🔧 DEBUG: Current section:', currentSection);
        console.log('🔧 DEBUG: Current field key:', currentFieldKey);
        
        // Apply field-specific content update if we have a field key
        if (currentFieldKey && (taskStatus as any)?.result) {
          try {
            console.log('🔧 DEBUG: Raw task result:', (taskStatus as any).result);
            console.log('🔧 DEBUG: Task result type:', typeof (taskStatus as any).result);
            
            let result;
            if (typeof (taskStatus as any).result === 'string') {
              result = JSON.parse((taskStatus as any).result);
            } else {
              result = (taskStatus as any).result;
            }
            
            console.log('🔧 DEBUG: Parsed result:', result);
            console.log('🔧 DEBUG: Result keys:', Object.keys(result));
            
            const fieldKey = `${currentSection}.${currentFieldKey}`;
            console.log('🔧 DEBUG: Looking for field key:', fieldKey);
            
            let newContent = result[fieldKey];
            console.log('🔧 DEBUG: New content found with primary key:', newContent);
            
            // Try alternative field key formats if primary key doesn't work
            if (!newContent) {
              console.log('🔧 DEBUG: Primary key not found, trying alternatives...');
              // Try just the field key
              newContent = result[currentFieldKey];
              console.log('🔧 DEBUG: New content with field key only:', newContent);
              
              // Try section name only
              if (!newContent) {
                newContent = result[currentSection];
                console.log('🔧 DEBUG: New content with section name only:', newContent);
              }
            }
            
            console.log('🔧 DEBUG: Final new content:', newContent);
            console.log('🔧 DEBUG: New content type:', typeof newContent);
            console.log('🔧 DEBUG: New content length:', newContent?.length || 0);
            
            if (newContent) {
              // Apply the new content to the specific field
              applyContentToField(currentSection, currentFieldKey, newContent);
              console.log(`✅ Applied field-specific content for ${fieldKey}`);
              
              // Force a re-render to show the updated content
              setForceUpdate(prev => prev + 1);
              
              // Show success message
              toast.success('Content regenerated successfully!');
              
              // Refresh history to show the new entry
              queryClient.invalidateQueries({ queryKey: ['creative-brief-history', projectId] });
            } else {
              console.warn('⚠️ No new content found in result for field:', fieldKey);
              toast.error('No new content generated');
            }
          } catch (error) {
            console.error('Error parsing task result:', error);
            toast.error('Failed to parse regeneration result');
          }
        } else if (!currentFieldKey) {
          // Section-level regeneration - refresh the entire dashboard
          console.log('🔧 DEBUG: Section-level regeneration, refreshing dashboard...');
          
          // Force a re-render to show the updated content
          setForceUpdate(prev => prev + 1);
          
          queryClient.invalidateQueries({ queryKey: ['research-dashboard', projectId] });
          queryClient.invalidateQueries({ queryKey: ['creative-brief-history', projectId] });
          toast.success('Section regenerated successfully!');
        }
        
        // Close popup after a short delay to show success message
        setTimeout(() => {
          setShowRegeneratePopup(false);
          setTaskId(null); // Clear task ID
          setFeedback(''); // Clear feedback
        }, 2000);
      } else if ((taskStatus as any)?.status === 'failed') {
        console.error('🔧 DEBUG: Task failed:', (taskStatus as any)?.error);
        toast.error(`Regeneration failed: ${(taskStatus as any)?.error || 'Unknown error'}`);
        setTaskId(null); // Clear task ID
      }
    }, [(taskStatus as any)?.status, projectId, queryClient, currentSection, currentFieldKey]);

    const handleRegenerate = async () => {
      // Allow regeneration without feedback; optional guidance only

      try {
        const finalFeedback = feedback.trim();
        const currentContent = getCurrentSectionContent();
        
        console.log('🔧 DEBUG: Frontend regenerate request:');
        console.log('🔧 DEBUG: - project_id:', projectId);
        console.log('🔧 DEBUG: - section_name:', currentSection);
        console.log('🔧 DEBUG: - field_key:', currentFieldKey);
        console.log('🔧 DEBUG: - field_key type:', typeof currentFieldKey);
        console.log('🔧 DEBUG: - feedback:', finalFeedback);
        console.log('🔧 DEBUG: - current_content length:', currentContent?.length || 0);
        console.log('🔧 DEBUG: - current_content preview:', currentContent?.substring(0, 100) + '...');
        
        // Validate field key before sending
        if (!currentFieldKey) {
          console.error('🔧 ERROR: No field key provided for regeneration');
          toast.error('No field selected for regeneration');
          return;
        }
        
        // Call the real regenerate API
        const result = await regenerateSection.mutateAsync({
          project_id: projectId || '',
          section_name: currentSection,
          field_key: currentFieldKey,
          feedback: finalFeedback || undefined,
          current_content: currentContent
        });
        
        // Set the task ID to start polling
        setTaskId(result.task_id);
        
        toast.success(`Section regeneration started! Task ID: ${result.task_id}`);
        
        // Don't close the popup yet - wait for completion
      } catch (error) {
        console.error('Regeneration error:', error);
        toast.error('Failed to start regeneration. Please try again.');
      }
    };

    const getCurrentSectionName = () => {
      if (lastFocusedLabel) return `${currentSection} — ${lastFocusedLabel}`;
      return currentSection;
    };

    const getCurrentSectionContent = () => {
      // Field-level overrides for precise preview
      if (currentSection === 'Product Snapshot') {
        switch (currentFieldKey) {
          case 'productName': return projectName;
          case 'productDescription': return productDescription;
          case 'problemSolved': return problemSolved;
          case 'targetAudience': return targetAudience2;
          default: return productDescription;
        }
      }
      if (currentSection === 'Current Listing Challenges') {
        switch (currentFieldKey) {
          case 'currentChallenges': return currentChallenges;
          case 'conversionLoss': return conversionLoss;
          default: return conversionLoss || currentChallenges;
        }
      }
      if (currentSection === 'Target Customer Deep Dive') {
        // Provide field-specific preview for multi-question section
        switch (currentFieldKey) {
          case 'location': return location;
          case 'income': return income;
          case 'profession': return profession;
          case 'lifeStage': return lifeStage;
          case 'painPoints': return painPoints;
          case 'shoppingBehavior': return shoppingBehavior;
          default: return targetAudience2 || location || income || profession || lifeStage || painPoints || shoppingBehavior;
        }
      }
      if (currentSection === 'Barriers To Purchase') {
        switch (currentFieldKey) {
          case 'conversionLoss': return conversionLoss;
          default: return conversionLoss;
        }
      }
      if (currentSection === 'Brand Voice & Tone') {
        // Two fields: main tone and signature phrases
        switch (currentFieldKey) {
          case 'toneOfVoice': return toneOfVoice;
          case 'toneOfVoice2': return toneOfVoice2;
          default: return toneOfVoice || toneOfVoice2;
        }
      }
      if (currentSection === 'USP(Unique SellingPropositions)') {
        switch (currentFieldKey) {
          case 'uniqueSellingPoints': return uniqueSellingPoints;
          case 'functionalBenefits': return functionalBenefits;
          default: return uniqueSellingPoints;
        }
      }
      if (currentSection === '5-Second Wow Factor') {
        switch (currentFieldKey) {
          case 'painPoints': return painPoints;
          default: return painPoints;
        }
      }
      if (currentSection === 'Key Features (With Context)') {
        switch (currentFieldKey) {
          case 'keyFeatures': return keyFeatures;
          default: return keyFeatures;
        }
      }
      if (currentSection === 'Top 6 Selling Points (With Strategic Justification)') {
        switch (currentFieldKey) {
          case 'functionalBenefits': return top6SellingPoints;
          default: return top6SellingPoints;
        }
      }
      if (currentSection === 'Competitive Landscape') {
        switch (currentFieldKey) {
          case 'competitorComparison': return competitorComparison;
          case 'amazonDifferentiators': return amazonDifferentiators;
          default: return competitorComparison;
        }
      }
      if (currentSection === 'Search & Keywords Strategy') {
        switch (currentFieldKey) {
          case 'searchKeywords': return searchKeywords;
          default: return searchKeywords;
        }
      }
      if (currentSection === 'Brand Story, Values & Purpose') {
        switch (currentFieldKey) {
          case 'brandStory': return brandStory;
          case 'brandStory2': return brandStory2;
          default: return brandStory || brandStory2;
        }
      }
      if (currentSection === 'Design Direction') {
        switch (currentFieldKey) {
          case 'designDirection': return designDirection;
          case 'designDirection2': return designDirection2;
          default: return designDirection || designDirection2;
        }
      }
      if (currentSection === 'Final Notes & Strategic Callouts') {
        switch (currentFieldKey) {
          case 'finalNotes': return finalNotes;
          default: return finalNotes;
        }
      }
      if (currentSection === 'Project Overview') {
        return projectName;
      }
      return 'No content available';
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 relative max-h-[90vh] overflow-y-auto" style={{ border: '1px solid #E2D5FF' }}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold text-[#272264]">
              {getCurrentSectionName()}
            </h3>
            <button 
              type="button"
              onClick={() => setShowRegeneratePopup(false)}
              disabled={!!taskId && (taskStatus as any)?.status === 'processing'}
              className="text-gray-500 hover:text-gray-700 text-2xl font-bold disabled:opacity-50"
            >
              ×
            </button>
          </div>

          {/* Current Content Preview */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Content Preview
            </label>
            <div className="bg-gray-50 p-4 rounded-lg max-h-32 overflow-y-auto text-sm text-gray-600 whitespace-pre-wrap break-words">
              {typeof getCurrentSectionContent() === 'string' ? getCurrentSectionContent() : JSON.stringify(getCurrentSectionContent(), null, 2)}
            </div>
          </div>

          {/* Prompt Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Prompt (Optional)
            </label>
            <textarea 
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              onInput={handleTextareaResize}
              className="w-full p-4 border border-gray-300 rounded-lg resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]"
              placeholder="Write guidance for regeneration, e.g. tone, details to emphasize, audience nuances..."
            />
          </div>

          {/* Custom prompt merged into feedback; removed extra UI */}

          {/* Task Status Display */}
          {taskId && (taskStatus as any) && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-blue-800">Regeneration Progress</h4>
                <span className="text-xs text-blue-600">Task ID: {taskId}</span>
              </div>
              
              {(taskStatus as any).status === 'processing' || (taskStatus as any).status === undefined ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm text-blue-700">Processing...</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${Math.max(10, Math.min(100, Number((taskStatus as any)?.progress || 10)))}%` }}
                    ></div>
                  </div>
                  {(taskStatus as any).message && (
                    <p className="text-xs text-blue-600">{(taskStatus as any).message}</p>
                  )}
                </div>
              ) : null}
              
              {(taskStatus as any).status === 'completed' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-green-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm font-medium">Regeneration Complete!</span>
                  </div>
                  <p className="text-xs text-green-600">The section has been successfully regenerated.</p>
                  {/* Auto-refresh content in UI by invalidating cache */}
                  <button 
                    type="button"
                    onClick={(e) => { e.preventDefault(); setShowRegeneratePopup(false); }}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700"
                  >
                    Close
                  </button>
                </div>
              )}
              
              {(taskStatus as any).status === 'failed' && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-red-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-sm font-medium">Regeneration Failed</span>
                  </div>
                  <p className="text-xs text-red-600">{(taskStatus as any).error || 'An error occurred during regeneration.'}</p>
                  <button 
                    onClick={() => setTaskId(null)}
                    className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-4">
            <button 
              type="button"
              onClick={() => setShowRegeneratePopup(false)}
              disabled={!!taskId && (taskStatus as any)?.status === 'processing'}
              className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button 
              type="button"
              onClick={handleRegenerate}
              disabled={!!taskId && (taskStatus as any)?.status === 'processing'}
              className="px-6 py-3 bg-gradient-to-b from-[#5A4ABE] to-[#362E7F] text-white font-medium rounded-lg shadow-md hover:from-[#4A3AAE] hover:to-[#2E2E6F] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {regenerateSection.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Starting...
                </>
              ) : taskId && (taskStatus as any)?.status === 'processing' ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Regenerating...
                </>
              ) : taskId && (taskStatus as any)?.status === 'failed' ? (
                'Retry Regeneration'
              ) : (
                'Start Regeneration'
              )}
            </button>
          </div>

          {/* Tips */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-800 mb-2">💡 Tips for better results:</h4>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>• Be specific about what you want to change</li>
              <li>• Mention tone, style, or target audience preferences</li>
              <li>• Use custom prompts for highly specific requirements</li>
              <li>• Provide examples of what you're looking for</li>
            </ul>
          </div>
        </div>
      </div>
    );
  };

  // History Popup
  const HistoryPopup = () => {
    const [selectedSection, setSelectedSection] = useState(currentSection || 'all');
    const [applyTarget, setApplyTarget] = useState<string | null>(currentFieldKey);
    
    // Use real history data from API
    const { data: historyResponse, isLoading: isHistoryLoading, error: historyError, refetch: refetchHistory } = useCreativeBriefHistory(
      projectId || null,
      selectedSection === 'all' ? undefined : selectedSection
    );
    
    // Refresh history when popup opens
    useEffect(() => {
      if (showHistoryPopup) {
        refetchHistory();
      }
    }, [showHistoryPopup, refetchHistory]);
    
    const historyData = extractHistoryList(historyResponse);
    const sections: string[] = ['all', ...Array.from(new Set((historyData as any[]).map((item: any) => {
      // Handle different possible field names for section
      const sectionName = item.section || item.section_name || item.sectionName || '';
      // For field-specific entries, extract the main section name
      return sectionName.includes('.') ? sectionName.split('.')[0] : sectionName;
    }))).filter(Boolean)];

    const filteredHistory = selectedSection === 'all'
      ? (historyData as any[])
      : (historyData as any[]).filter((item: any) => {
          const itemSection = item.section || item.section_name || item.sectionName || '';
          // For field-specific entries, check if the main section matches
          const mainSection = itemSection.includes('.') ? itemSection.split('.')[0] : itemSection;
          return mainSection === selectedSection;
        });

    const formatHistoryContent = (val: any) => {
      console.log('🔧 DEBUG: formatHistoryContent input:', val);
      if (val === null || val === undefined) {
        console.log('🔧 DEBUG: Content is null/undefined');
        return '';
      }
      if (typeof val === 'string') {
        try {
          const obj = JSON.parse(val);
          const result = JSON.stringify(obj, null, 2);
          console.log('🔧 DEBUG: Parsed JSON content:', result.substring(0, 100));
          return result;
        } catch {
          console.log('🔧 DEBUG: Using string content as-is:', val.substring(0, 100));
          return val;
        }
      }
      try {
        const result = JSON.stringify(val, null, 2);
        console.log('🔧 DEBUG: Stringified object content:', result.substring(0, 100));
        return result;
      } catch {
        const result = String(val);
        console.log('🔧 DEBUG: Converted to string:', result.substring(0, 100));
        return result;
      }
    };

    const getChangeTypeIcon = (changeType: string) => {
      switch (changeType) {
        case 'regeneration':
          return '🔄';
        case 'regeneration_with_prompt':
          return '✨';
        case 'manual_edit':
          return '✏️';
        default:
          return '📝';
      }
    };

    const getChangeTypeLabel = (changeType: string) => {
      switch (changeType) {
        case 'regeneration':
          return 'AI Regeneration';
        case 'regeneration_with_prompt':
          return 'Custom Prompt';
        case 'manual_edit':
          return 'Manual Edit';
        default:
          return 'Change';
      }
    };

    const applyHistoryContent = (text: string) => {
      const value = typeof text === 'string' ? text : (JSON.stringify(text) || '');
      const fieldKey = applyTarget || currentFieldKey;
      
      if (fieldKey && currentSection) {
        // Use the unified applyContentToField function
        applyContentToField(currentSection, fieldKey, value);
      } else {
        console.warn('Missing section or field key for history application');
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-4xl w-full mx-4 relative max-h-[90vh] overflow-y-auto" style={{ border: '1px solid #E2D5FF' }}>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5 text-[#6B4EFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <h3 className="text-lg font-semibold text-[#272264]">CONTENT HISTORY</h3>
            </div>
            <button 
              onClick={() => setShowHistoryPopup(false)}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>
          </div>

          {/* Section Filter */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Section</label>
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]"
            >
              {sections.map((section: string) => (
                <option key={String(section)} value={section}>
                  {section === 'all' ? 'All Sections' : section}
                </option>
              ))}
            </select>
            {/* Field/Box target selector visible only for Product Snapshot and Current Listing Challenges */}
            {(currentSection === 'Product Snapshot' || currentSection === 'Current Listing Challenges') && (
              <div className="mt-3">
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply to Field</label>
                <select
                  value={applyTarget || ''}
                  onChange={(e) => setApplyTarget(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B9A7FF]"
                >
                  <option value="">Auto-detect</option>
                  {currentSection === 'Product Snapshot' && (
                    <>
                      <option value="productName">What exactly is the product?</option>
                      <option value="productDescription">What does it do and how does it work?</option>
                      <option value="problemSolved">What problem does it solve?</option>
                      <option value="targetAudience">Who is it meant for?</option>
                    </>
                  )}
                  {currentSection === 'Current Listing Challenges' && (
                    <>
                      <option value="currentChallenges">What’s broken/underwhelming?</option>
                      <option value="conversionLoss">Barriers to purchase</option>
                    </>
                  )}
                </select>
              </div>
            )}
          </div>

          {/* History List - rows for previous and new */}
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {isHistoryLoading ? (
              <div className="text-center py-8 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6B4EFF] mx-auto mb-4"></div>
                <p>Loading history...</p>
              </div>
            ) : historyError ? (
              <div className="text-center py-8 text-red-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p>Failed to load history</p>
                <p className="text-sm text-gray-500 mt-2">{historyError.message || 'Unknown error'}</p>
              </div>
            ) : filteredHistory.length > 0 ? (
              filteredHistory.map((item: any) => {
                console.log('🔧 DEBUG: History item:', item);
                return (
                <div key={String(item.id)} className="bg-white border border-[#E2D5FF] rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getChangeTypeIcon(item.change_type)}</span>
                      <span className="text-sm font-medium text-[#6B4EFF] bg-[#F0EDFF] px-2 py-1 rounded-full">
                        {getChangeTypeLabel(item.change_type)}
                      </span>
                      <span className="text-sm font-medium text-gray-600">{item.section}</span>
                    </div>
                    <span className="text-xs text-gray-500">{new Date(item.timestamp).toLocaleString()}</span>
                  </div>
                  {item.feedback && (
                    <div className="mb-2 text-xs text-gray-700"><span className="font-medium">Feedback:</span> {item.feedback}</div>
                  )}
                  {item.ai_prompt && (
                    <div className="mb-2 text-xs text-gray-700 whitespace-pre-wrap break-words"><span className="font-medium">AI Prompt:</span> {item.ai_prompt}</div>
                  )}
                  <div className="mb-2">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Previous</label>
                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded max-h-24 overflow-y-auto whitespace-pre-wrap break-words">{formatHistoryContent(item.previous_content)}</div>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        className="px-3 py-1 text-xs rounded border border-gray-300 hover:bg-gray-50"
                        onClick={(e) => { e.preventDefault(); applyHistoryContent(formatHistoryContent(item.previous_content)); setShowHistoryPopup(false); }}
                      >
                        Use Previous
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">New</label>
                    <div className="text-xs text-gray-600 bg-green-50 p-2 rounded max-h-24 overflow-y-auto whitespace-pre-wrap break-words">{formatHistoryContent(item.new_content)}</div>
                    <div className="mt-2 flex gap-2">
                      <button
                        type="button"
                        className="px-3 py-1 text-xs rounded border border-green-300 text-green-700 hover:bg-green-50"
                        onClick={(e) => { e.preventDefault(); applyHistoryContent(formatHistoryContent(item.new_content)); setShowHistoryPopup(false); }}
                      >
                        Use New
                      </button>
                    </div>
                  </div>
                </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>No history found for this section</p>
              </div>
            )}
          </div>

          {/* Summary */}
          <div className="mt-6 p-4 bg-[#F0EDFF] rounded-lg">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[#6B4EFF] font-medium">
                Total Changes: {filteredHistory.length}
              </span>
              <span className="text-gray-600">
                Last updated: {filteredHistory[0]?.timestamp || 'Never'}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>
        {`
          .stepper-container::-webkit-scrollbar {
            display: none;
          }
          
          .main-content::-webkit-scrollbar {
            width: 8px;
          }
          
          .main-content::-webkit-scrollbar-track {
            background: transparent;
            margin: 4px;
          }
          
          .main-content::-webkit-scrollbar-thumb {
            background: #DACAFF;
            border-radius: 20px;
            border: 2px solid transparent;
            background-clip: content-box;
          }
          
          .main-content::-webkit-scrollbar-thumb:hover {
            background: #C5B5FF;
            background-clip: content-box;
          }
          
          /* Force all textareas to auto-resize */
        textarea {
          min-height: 110px !important;
          max-height: none !important;
          overflow: hidden !important;
          resize: none !important;
          box-sizing: border-box !important;
        }
        `}
      </style>
      <div className="min-h-screen w-full flex flex-col items-center py-12 px-2" style={{ background: 'linear-gradient(248deg, #B391E8 6.01%, #FDFDFF 98.77%)' }}>
      {/* Heading and Created On */}
      <div className="w-full max-w-7xl flex flex-col items-center mb-8">
        <h2 className="text-[2.25rem] font-extrabold tracking-wide text-[#5B4B9A] uppercase mb-2 text-center" style={{letterSpacing: '0.04em', fontFamily: 'Inter, sans-serif'}}>
          ONBOARDING BRIEF: {projectName ? projectName.toUpperCase() : '—'}
        </h2>
        <div className="text-base text-[#272264] font-semibold text-center">Created On : <span className="font-normal">{new Date().toLocaleDateString()}</span></div>
      </div>
      <div className="w-full max-w-7xl flex gap-8">
        {/* Stepper */}
        <div
          className="flex flex-col items-start text-white shadow-xl relative p-8 overflow-y-auto stepper-container"
          style={{
            width: '371px',
            height: '657px',
            borderRadius: '12px',
            border: '1px solid #9D6DEC',
            background: 'linear-gradient(134deg, rgba(63, 53, 143, 0.80) -7.57%, rgba(41, 25, 127, 0.80) 44.71%, rgba(89, 59, 165, 0.80) 106.9%)',
            backdropFilter: 'blur(15px)',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {/* Vertical line - perfectly centered behind the icons */}
          <div className="absolute z-0 bottom-6 left-0" style={{ top: '2rem', left: '53px', width: '4px', background: 'rgba(255,255,255,0.3)', borderRadius: '3px', height: '1320px' }} />
          {/* Progress line - green up to last approved step */}
          {(() => {
            const maxApproved = approvedSteps.size > 0 ? Math.max(...Array.from(approvedSteps)) : -1;
            // Ensure the line fills up to the next step's circle position
            // Step height is 48px (gap-12) + some additional spacing, so using 96px for proper circle alignment
            const progressHeight = Math.min((maxApproved + 1) * 96, steps.length * 96);
            console.log('Green line calculation:', { maxApproved, progressHeight, approvedSteps: Array.from(approvedSteps) });
            return (
              <div className="absolute z-0" style={{ 
                top: '2rem', 
                left: '53px', 
                width: '4px', 
                height: `${progressHeight}px`, 
                background: '#4ADE80', 
                borderRadius: '3px' 
              }} />
            );
          })()}



          <div className="flex flex-col gap-12 w-full relative z-10">
            {steps.map((step, idx) => {
              let status: 'done' | 'current' | 'upcoming' = 'upcoming';
              if (approvedSteps.has(idx)) status = 'done';
              else if (idx === activeStep) status = 'current';
              return (
                <div 
                  key={step} 
                  className="flex items-start gap-4 relative"
                >
                  <div className="flex flex-col items-center min-w-[3rem]">
                    <StepIcon status={status} />
                  </div>
                  <div className="flex flex-col justify-center">
                    <span className={`uppercase font-normal tracking-wide mb-1 ${status === 'current' ? 'text-white' : 'text-white/70'}`} style={{fontSize: '12px'}}>STEP {idx+1}</span>
                    <span className={`${status === 'current' ? 'font-extrabold text-white' : 'font-semibold text-white/70'}`} style={{fontSize: '16px', lineHeight: '1.2'}}>{step}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_0_rgba(107,78,255,0.10)] relative p-10 flex-1 overflow-y-auto main-content" style={{ width: '765px', height: '657px' }}>
          {/* Back Button */}
          {/* Disabled generic back button to prevent interference */}
          {/* {activeStep > 0 && (
            <button onClick={() => setActiveStep(activeStep - 1)} className="absolute left-10 top-8 text-[#6B4EFF] font-semibold flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M5 12L19 12M5 12L11 6M5 12L11 18" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          )} */}
          {/* Only render the current step's content once */}
          {(() => {
            console.log('Rendering step content - activeStep:', activeStep, 'stepContents.length:', stepContents.length);
            return activeStep < stepContents.length ? stepContents[activeStep] : <div>Step {activeStep + 1} content not found</div>;
          })()}
        </div>
      </div>
      {/* Popups */}
      {showRegeneratePopup && <RegeneratePopup />}
      {showHistoryPopup && <HistoryPopup />}
    </div>
    </>
  );
};

export default CreativeBriefPage; 